import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { format, addDays, subDays, parseISO } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  ChevronLeft,
  ChevronRight,
  Save,
  Printer,
  Plus,
  Trash2,
  RefreshCw,
  Store,
  Wand2,
  Undo2,
  Redo2,
  Eraser,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DateWithDataPicker } from "@/components/DateWithDataPicker";
import { ExportButton } from "@/components/ExportButton";
import { useAuth } from "@/context/AuthContext";
import ItemTrackerReport from "@/components/ItemTrackerReport";
import MonthlyDataSheet from "@/components/MonthlyDataSheet";
import { readSharedDate, writeSharedDate } from "@/lib/sharedDate";
import { DateWarningDialog, isNotRecentDay } from "@/components/DateWarningDialog";
import IncomeExpenseTab from "@/components/IncomeExpenseTab";
import ProfitLossTab from "@/components/ProfitLossTab";
import AccountStatementTab from "@/components/AccountStatementTab";
import { useFlushOnHide, keepaliveSave } from "@/hooks/useFlushOnHide";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Field defaults — SAFE FALLBACK ONLY.
// New sheets seed from the admin's Store Paperwork template
// (`GET /api/store-paperwork-template`). These hardcoded arrays are only
// used when that fetch fails or the template is empty (brand-new
// deployments before any admin has saved a template yet). Keep them
// deliberately minimal so we don't re-introduce rows that admins have
// intentionally removed (like the old "First Data Total (Machine 1)").
const DEFAULT_DEBIT_ROWS = [
  { name: "Instant Lottery", amount: "" },
  { name: "Online Lottery Sale", amount: "" },
  { name: "Beer", amount: "" },
  { name: "Tobacco", amount: "" },
  { name: "FEE", amount: "" },
];

const DEFAULT_CREDIT_ROWS = [
  { name: "Network Revenue", amount: "" },
  { name: "Coupons", amount: "" },
  { name: "Instant Paid Out", amount: "" },
];

// NOTE: Supplier rows use the new schema { id, vendor_id, vendor_name, method, amount }.
// `method` MUST be one of SUPPLIER_METHODS ("cash" | "check" | "ach") so the
// Cash-Paid-Out roll-up (see `supplierCashTotal` below) can filter reliably.
// Fresh accounts start with an empty list — users add rows via the UI.
const DEFAULT_SUPPLIER_ROWS = [];

const FUEL_GRADES = [
  { key: "unl", label: "UNL Regular" },
  { key: "plus", label: "Plus" },
  { key: "premium", label: "Premium" },
  { key: "diesel", label: "Diesel" },
];

// Helpers --------------------------------------------------------------------
const num = (v) => {
  const n = parseFloat(String(v || "").replace(/[^0-9.\-]/g, ""));
  return isFinite(n) ? n : 0;
};
const fmtMoney = (v) =>
  num(v).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const fmtMoneySigned = (v) => {
  const n = num(v);
  const abs = Math.abs(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
  if (Math.abs(n) < 0.005) return abs;
  return n < 0 ? `(${abs})` : `+${abs}`;
};
const fmtNum = (v, digits = 3) =>
  num(v).toLocaleString("en-US", { maximumFractionDigits: digits });

// Tax classification + auto-rows ------------------------------------------
// Department rows are tagged via their NAME SUFFIX:
//   "Cigarettes (T)"        → STATE_TAXABLE   (counts toward State Tax base only)
//   "Beer Cooler (Beer T)"  → BEER_TAXABLE    (counts toward BOTH State Tax AND City Beer Tax)
//   anything else           → REGULAR        (not taxed)
// Two pinned auto-rows sit at the BOTTOM of the Debit list:
//   _kind="state_tax_auto" name="State Tax (N%)"      amount = N% × sum(STATE_TAXABLE + BEER_TAXABLE)
//   _kind="beer_tax_auto"  name="City Beer Tax (N%)"  amount = N% × sum(BEER_TAXABLE)
// (Beer T) precedence: tested FIRST so a row with both "(T)" and "(Beer T)" is
// classified as beer-taxable (which now still incurs state tax too).
const TAX_BEER_TAXABLE = "beer_taxable";
const TAX_STATE_TAXABLE = "state_taxable";
const TAX_REGULAR = "regular";
const STATE_TAX_KIND = "state_tax_auto";
const BEER_TAX_KIND = "beer_tax_auto";
// Vendors Cash Paid Out — a pinned read-only debit row whose amount is the
// sum of cash-method supplier rows entered in the new Supplier Section.
const VENDORS_CASH_KIND = "vendors_cash_auto";
// Total Fuel — pinned read-only Debit row whose amount is the sum of every
// Sold Amount across all rows in the Fuel Tank Report section below.
const TOTAL_FUEL_KIND = "total_fuel_auto";
const RE_BEER_TAXABLE = /\(\s*beer\s*t\)\s*$/i;
const RE_STATE_TAXABLE = /\(\s*t\)\s*$/i;

// Default fuel rows for the Fuel Tank Report / Tank Monitor section. Fixed
// list of the 5 standard grades. Each row carries both the sales-side data
// (sold_volume/sold_amount/sign_price/cost_price) and the tank-monitor data
// (in_tank_volume/fuel_delivery). Sell Price is a per-row read-only compute
// (sold_amount / sold_volume). O/S is a per-row read-only compute
// (in_tank_volume + fuel_delivery - sold_volume).
const DEFAULT_FUEL_ROWS = [
  { id: "f-unl-regular", name: "UNL Regular",    sold_volume: "", sold_amount: "", sign_price: "", cost_price: "", in_tank_volume: "", fuel_delivery: "" },
  { id: "f-plus",        name: "Plus",           sold_volume: "", sold_amount: "", sign_price: "", cost_price: "", in_tank_volume: "", fuel_delivery: "" },
  { id: "f-premium",     name: "Premium",        sold_volume: "", sold_amount: "", sign_price: "", cost_price: "", in_tank_volume: "", fuel_delivery: "" },
  { id: "f-diesel",      name: "Diesel",         sold_volume: "", sold_amount: "", sign_price: "", cost_price: "", in_tank_volume: "", fuel_delivery: "" },
  { id: "f-kerosene",    name: "Kerosene",       sold_volume: "", sold_amount: "", sign_price: "", cost_price: "", in_tank_volume: "", fuel_delivery: "" },
];

// Payment methods for the Supplier Section
const SUPPLIER_METHODS = ["cash", "check", "ach"];
const SUPPLIER_METHOD_LABELS = { cash: "Cash", check: "Check", ach: "ACH" };

// Lottery-auto rows: any Debit row whose NAME matches one of these patterns
// has its amount force-pulled from Daily Entry / Lottery Management via the
// /api/lottery-sales-by-denom/{date} endpoint. Cells are read-only with a
// distinct sky-blue background so cashiers know they're sourced from
// lottery management, not typed manually.
//   "Instant Lottery"       → instant_sale  (sum of every $N denomination — preferred)
//   "$N Instant Lottery"    → per_denom[N]  (legacy per-denom rows — still supported)
//   "Online Lottery Sale"   → online_sale
//   "Instant Paid Out"      → instant_paid_out
//   "Online Paid Out"       → online_paid_out
const RE_INSTANT_LOTTERY_TOTAL = /^instant\s+lottery\s*$/i;
const RE_LOTTERY_DENOM = /^\$?\s*(\d+)\s+instant\s+lottery\b/i;
const RE_ONLINE_LOTTERY_SALE = /^online\s+lottery\s+sale\b/i;
const RE_INSTANT_PAID_OUT = /^instant\s+paid\s*[- ]?\s*out\b/i;
const RE_ONLINE_PAID_OUT = /^online\s+paid\s*[- ]?\s*out\b/i;

// Returns the auto-filled amount for a lottery row, or null if the row is not
// a lottery row. `lotterySales` is the shape returned by the backend:
// { per_denom: {N: $}, instant_sale, online_sale, instant_paid_out, online_paid_out }.
function lookupLotteryAmount(name, lotterySales) {
  if (!name || !lotterySales) return null;
  const trimmed = String(name).trim();
  // Aggregate "Instant Lottery" row → sum of every $N denomination.
  // Checked BEFORE the per-denom regex because "Instant Lottery" (no $N)
  // would fail the per-denom match anyway; explicit ordering avoids future
  // regex refactor accidents.
  if (RE_INSTANT_LOTTERY_TOTAL.test(trimmed)) return Number(lotterySales.instant_sale ?? 0);
  const md = trimmed.match(RE_LOTTERY_DENOM);
  if (md) {
    const key = String(parseInt(md[1], 10));
    const perDenom = lotterySales.per_denom || {};
    return key in perDenom ? Number(perDenom[key]) : null;
  }
  if (RE_ONLINE_LOTTERY_SALE.test(trimmed)) return Number(lotterySales.online_sale ?? 0);
  if (RE_INSTANT_PAID_OUT.test(trimmed)) return Number(lotterySales.instant_paid_out ?? 0);
  if (RE_ONLINE_PAID_OUT.test(trimmed)) return Number(lotterySales.online_paid_out ?? 0);
  return null;
}

// Legacy alias — kept for backwards-compat callers that only care about
// whether a row is lottery-related (any of the 5 patterns).
function detectLotteryDenom(name) {
  if (!name) return null;
  const trimmed = String(name).trim();
  if (RE_INSTANT_LOTTERY_TOTAL.test(trimmed)) return "instant_sale";
  const md = trimmed.match(RE_LOTTERY_DENOM);
  if (md) return parseInt(md[1], 10);
  if (RE_ONLINE_LOTTERY_SALE.test(trimmed)) return "online_sale";
  if (RE_INSTANT_PAID_OUT.test(trimmed)) return "instant_paid_out";
  if (RE_ONLINE_PAID_OUT.test(trimmed)) return "online_paid_out";
  return null;
}
function applyAutoLotteryRows(rows, lotterySales) {
  if (!Array.isArray(rows) || !lotterySales) return rows;
  let changed = false;
  const out = rows.map((r) => {
    if (!r || !r.name) return r;
    const amt = lookupLotteryAmount(r.name, lotterySales);
    if (amt == null) return r;
    const nextAmount = !amt ? "" : String(Number(amt).toFixed(2));
    if (String(r.amount || "") === nextAmount) return r;
    changed = true;
    return { ...r, amount: nextAmount };
  });
  return changed ? out : rows;
}

// Structural fingerprint for the two row lists — used by the admin auto-save
// side-effect to detect whether row NAMES / ORDER / COUNT changed (which
// triggers the propagate-to-all-dates flow) vs. amounts-only edits (which
// don't need propagation). Ignores `_kind` auto rows and amount values.
function _rowStructureKey(data) {
  if (!data) return "";
  const debit = (data.debit_rows || [])
    .filter((r) => r && !r._kind)
    .map((r) => (r.name || "").trim().toLowerCase());
  const credit = (data.credit_rows || [])
    .filter((r) => r && !r._kind)
    .map((r) => (r.name || "").trim().toLowerCase());
  return `${debit.join("|")}##${credit.join("|")}`;
}

function classifyTaxableRow(name) {
  if (!name) return TAX_REGULAR;
  const trimmed = String(name).trim();
  if (RE_BEER_TAXABLE.test(trimmed)) return TAX_BEER_TAXABLE;
  if (RE_STATE_TAXABLE.test(trimmed)) return TAX_STATE_TAXABLE;
  return TAX_REGULAR;
}

// Idempotent: removes any existing auto rows, recomputes them from the
// remaining (regular + taxable) rows, and pins them at the very end. Returns
// the SAME array reference when nothing needs to change so it can be used
// safely inside a useEffect without looping.
//   Order at bottom of Debit:  [Total Fuel]  →  [State Tax]  →  [City Beer Tax]
// (Vendors Cash Paid Out is now pinned on the Credit side — see
// applyAutoVendorsCashCreditRow below.)
function applyAutoTaxRows(rows, statePct, beerPct, totalFuelAmount = 0) {
  const safe = Array.isArray(rows) ? rows : [];
  const base = safe.filter((r) => r && r._kind !== STATE_TAX_KIND && r._kind !== BEER_TAX_KIND && r._kind !== VENDORS_CASH_KIND && r._kind !== TOTAL_FUEL_KIND);

  let stateBase = 0;
  let beerBase = 0;
  for (const r of base) {
    const cls = classifyTaxableRow(r?.name);
    const amt = num(r?.amount);
    if (cls === TAX_STATE_TAXABLE) {
      stateBase += amt;
    } else if (cls === TAX_BEER_TAXABLE) {
      // Beer rows now get BOTH state tax AND city beer tax applied.
      stateBase += amt;
      beerBase += amt;
    }
  }
  const sp = num(statePct);
  const bp = num(beerPct);
  const stateAmt = (stateBase * sp) / 100;
  const beerAmt = (beerBase * bp) / 100;
  const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);
  const totalFuelRow = { _kind: TOTAL_FUEL_KIND, name: "Total Fuel", amount: fmt(num(totalFuelAmount)) };
  const stateRow = { _kind: STATE_TAX_KIND, name: `State Tax (${sp}%)`, amount: fmt(stateAmt) };
  const beerRow = { _kind: BEER_TAX_KIND, name: `City Beer Tax (${bp}%)`, amount: fmt(beerAmt) };

  const desired = [...base, totalFuelRow, stateRow, beerRow];
  if (safe.length === desired.length) {
    let same = true;
    for (let i = 0; i < safe.length; i++) {
      const a = safe[i] || {};
      const b = desired[i];
      if (a._kind !== b._kind || a.name !== b.name || String(a.amount) !== String(b.amount)) {
        same = false;
        break;
      }
    }
    if (same) return safe; // no-op — return same reference
  }
  return desired;
}

// Idempotent: pins the "Vendors Cash Paid Out" auto-row at the end of the
// Credit list. Cash-method supplier payments are a form of cash leaving the
// register in exchange for goods received, so they land on the Credit side
// alongside Instant/Online/Other Paid Out. Returns the SAME array reference
// when nothing changes so it can be used inside a useEffect without looping.
function applyAutoVendorsCashCreditRow(rows, vendorsCashTotal = 0) {
  const safe = Array.isArray(rows) ? rows : [];
  const base = safe.filter((r) => r && r._kind !== VENDORS_CASH_KIND);
  const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);
  const vc = num(vendorsCashTotal);
  const vendorsCashRow = { _kind: VENDORS_CASH_KIND, name: "Vendors Cash Paid Out", amount: fmt(vc) };
  const desired = [...base, vendorsCashRow];
  if (safe.length === desired.length) {
    let same = true;
    for (let i = 0; i < safe.length; i++) {
      const a = safe[i] || {};
      const b = desired[i];
      if (a._kind !== b._kind || a.name !== b.name || String(a.amount) !== String(b.amount)) {
        same = false;
        break;
      }
    }
    if (same) return safe;
  }
  return desired;
}

// Reusable single-row input (label left, $ input right) ---------------------
function FieldRow({ field, value, onChange, dollar = true, readOnly = false, hint, testIdPrefix = "sp" }) {
  return (
    <div className="grid grid-cols-[1fr,140px] items-center gap-2 py-1 border-b border-border/15 last:border-b-0">
      <span className="font-mono text-xs text-card-foreground" title={hint}>
        {field.label}
        {hint && <span className="ml-1 text-[10px] text-muted-foreground">({hint})</span>}
      </span>
      <div className="flex items-center gap-1">
        {dollar && <span className="text-xs text-muted-foreground">$</span>}
        <Input
          inputMode="decimal"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          className={`h-7 text-right text-xs font-mono tabular-nums !text-black !border-stone-400 ${readOnly ? "!bg-yellow-50 cursor-not-allowed" : "!bg-yellow-100"}`}
          data-testid={`${testIdPrefix}-${field.key}`}
          placeholder="0.00"
        />
      </div>
    </div>
  );
}

// Editable row list (label + amount, add/remove rows, auto-total) -----------
// Used for Debit, Credit, Suppliers, and Cash Paid Out. Single shared widget
// so the four sections behave identically.
//
// Optional `getRowMeta(row, index)` returns `{ readOnlyAmount: bool, badge?: string }`.
// When `readOnlyAmount` is true, the amount input is locked and styled to
// indicate it's auto-computed (used by the Debit section for (T) / Beer T)
// rows).
//
// `templateNames` (optional): a Set of lowercased row names that belong to
// the admin-controlled shared template. When provided, ONLY rows whose name
// is in this Set are treated as read-only (name locked + delete hidden). Any
// other rows are considered user-added custom rows and remain fully editable.
// If null/undefined (admin view), no name-based restriction is applied.
function EditableRowList({ rows, onChange, testIdPrefix = "row", placeholderName = "Name", labelHeader = "Name", showDollar = true, getRowMeta, locked = false, templateNames = null }) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const updateRow = (i, patch) => {
    const next = safeRows.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const addRow = () => onChange([...safeRows, { name: "", amount: "" }]);
  const removeRow = (i) => onChange(safeRows.filter((_, idx) => idx !== i));
  const total = safeRows.reduce((s, r) => s + num(r.amount), 0);

  return (
    <div data-testid={`${testIdPrefix}-list`}>
      <div className="grid grid-cols-[1fr,140px,32px] gap-2 pb-1 text-[10px] font-mono uppercase text-muted-foreground tracking-wider">
        <span>{labelHeader}</span>
        <span className="text-right">Amount</span>
        <span />
      </div>
      <div className="space-y-1">
        {safeRows.length === 0 && (
          <p className="text-xs font-mono text-muted-foreground text-center py-2">No entries yet — click + Add Row</p>
        )}
        {safeRows.map((row, i) => {
          const baseMeta = getRowMeta ? (getRowMeta(row, i) || {}) : {};
          // A row is a "template row" (name-locked + non-deletable) when
          //   • the whole list is locked (legacy behaviour), OR
          //   • templateNames is provided AND this row's name is in the set.
          // Anything else (custom user-added row) stays fully editable.
          const nameKey = (row?.name || "").trim().toLowerCase();
          const isTemplateRow = templateNames ? templateNames.has(nameKey) : false;
          const shouldLock = locked || isTemplateRow;
          const meta = shouldLock
            ? { ...baseMeta, readOnlyName: true, hideDelete: true }
            : baseMeta;
          const readOnly = !!meta.readOnlyAmount;
          const readOnlyName = !!meta.readOnlyName;
          const hideDelete = !!meta.hideDelete;
          return (
            <div key={i} className="grid grid-cols-[1fr,140px,32px] gap-2 items-center">
              <Input
                value={row.name || ""}
                onChange={(e) => updateRow(i, { name: e.target.value })}
                placeholder={placeholderName}
                readOnly={readOnlyName}
                className={`h-7 text-xs font-mono !text-black !border-stone-600 placeholder:!text-stone-800 ${
                  readOnlyName
                    ? (meta.readOnlyVariant === "lottery"
                        ? "!bg-sky-300 font-bold cursor-not-allowed"
                        : "!bg-amber-200 font-bold cursor-not-allowed")
                    : "!bg-stone-500"
                }`}
                data-testid={`${testIdPrefix}-name-${i}`}
              />
              <div className="flex items-center gap-1 relative">
                {showDollar && <span className="text-xs text-muted-foreground">$</span>}
                <Input
                  inputMode="decimal"
                  value={row.amount || ""}
                  onChange={(e) => updateRow(i, { amount: e.target.value })}
                  placeholder="0.00"
                  readOnly={readOnly}
                  title={meta.badge || ""}
                  className={`h-7 text-right text-xs font-mono tabular-nums !text-black !border-stone-400 placeholder:!text-stone-500 ${
                    readOnly
                      ? (meta.readOnlyVariant === "lottery"
                          ? "!bg-sky-200 cursor-not-allowed font-bold"
                          : "!bg-amber-200 cursor-not-allowed font-bold")
                      : "!bg-yellow-100"
                  }`}
                  data-testid={`${testIdPrefix}-amt-${i}`}
                />
                {readOnly && meta.badge && (
                  <span
                    className={`absolute -top-2 right-0 text-[8px] font-mono font-bold rounded px-1 leading-none pointer-events-none ${
                      meta.readOnlyVariant === "lottery"
                        ? "bg-sky-500 text-black"
                        : "bg-amber-500 text-black"
                    }`}
                    data-testid={`${testIdPrefix}-badge-${i}`}
                  >
                    {meta.badge}
                  </span>
                )}
              </div>
              {hideDelete ? (
                <div className="h-7 w-7" />
              ) : (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => removeRow(i)}
                  className="h-7 w-7 text-rose-400 hover:text-rose-300"
                  title="Remove row"
                  data-testid={`${testIdPrefix}-del-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/30">
        {locked ? (
          <span className="text-[10px] font-mono text-muted-foreground italic">Row list managed by admin · contact admin to add/remove rows</span>
        ) : (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={addRow} className="btn-3d h-7" data-testid={`${testIdPrefix}-add`}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Row
            </Button>
            {templateNames && templateNames.size > 0 && (
              <span className="text-[10px] font-mono text-muted-foreground italic">
                Template rows are locked · add your own custom rows freely
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-muted-foreground">Total</span>
          <span className="font-mono font-bold tabular-nums text-primary" data-testid={`${testIdPrefix}-total`}>{fmtMoney(total)}</span>
        </div>
      </div>
    </div>
  );
}

// Main page ------------------------------------------------------------------
export default function StorePaperwork() {
  const { date: paramDate } = useParams();
  const navigate = useNavigate();

  // Defensive: parseISO returns an Invalid Date for bad strings; format() on
  // an Invalid Date throws RangeError and unmounts the route (blank screen).
  // Fall back to today if the param is unusable or absent.
  const safeInitialDate = (() => {
    if (!paramDate) return readSharedDate() || new Date();
    try {
      const d = parseISO(paramDate);
      return isNaN(d.getTime()) ? new Date() : d;
    } catch {
      return new Date();
    }
  })();

  const [selectedDate, setSelectedDate] = useState(safeInitialDate);
  const dateStr = (() => {
    try {
      return format(selectedDate, "yyyy-MM-dd");
    } catch {
      return format(new Date(), "yyyy-MM-dd");
    }
  })();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [dataRefreshKey, setDataRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState("z-report");
  const [highlightIeId, setHighlightIeId] = useState(null);
  // Per-denomination lottery sale $ pulled from Daily Entry for this date.
  // Applied read-only to any Debit row whose name matches "$N Instant Lottery".
  const [lotterySales, setLotterySales] = useState(null); // null = not yet fetched, {} = no data
  // Master list of vendors (per-user, editable in Setup tab) — used to populate
  // the Supplier Section dropdown on the Z-Report.
  const [vendors, setVendors] = useState([]);
  // Tax settings: per-user with admin defaults as fallback
  const [taxSettings, setTaxSettings] = useState({
    state_tax_percent: 6,
    beer_tax_percent: 5,
    is_user_override: { state_tax_percent: false, beer_tax_percent: false },
    defaults: { state_tax_percent: 6, beer_tax_percent: 5 },
  });
  const [taxSaving, setTaxSaving] = useState(false);
  // Admin-curated row template. Regular users get locked rows from this; admins
  // are NOT locked (they can still add/delete on their personal daily entry).
  const [template, setTemplate] = useState({ debit_rows: [], credit_rows: [] });
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  // Lowercased Sets of template row names — used by EditableRowList to render
  // template rows as read-only (name locked + delete hidden) while letting
  // users freely add/remove their own custom rows alongside them. Admins get
  // `null` (no template restriction — they control everything).
  const debitTemplateNames = useMemo(() => {
    if (isAdmin) return null;
    const s = new Set();
    for (const t of template.debit_rows || []) {
      const k = (t?.name || "").trim().toLowerCase();
      if (k) s.add(k);
    }
    return s;
  }, [template.debit_rows, isAdmin]);
  const creditTemplateNames = useMemo(() => {
    if (isAdmin) return null;
    const s = new Set();
    for (const t of template.credit_rows || []) {
      const k = (t?.name || "").trim().toLowerCase();
      if (k) s.add(k);
    }
    return s;
  }, [template.credit_rows, isAdmin]);
  const saveTimerRef = useRef(null);
  const isFirstLoadRef = useRef(true);
  // Stable ref to the latest saveNow + lastSavedSnapshot — prevents the
  // auto-save useEffect from re-firing on every keystroke (which used to
  // cause the Save button to flash + sometimes drop saves under race
  // conditions). We compare JSON-stringified data against the last
  // successfully-saved snapshot and skip if unchanged.
  const saveNowRef = useRef(() => {});
  const lastSavedSnapshotRef = useRef(null);
  // Refs to admin sync helpers so the auto-save useEffect can call them
  // without listing them as deps (they get redefined every render).
  const syncLayoutToAllDatesRef = useRef(() => Promise.resolve(null));
  const pushLayoutToSharedTemplateRef = useRef(() => Promise.resolve(null));
  // Tracks the last row-structure snapshot we've already propagated
  // (template + sync). When only amounts change we skip re-propagating —
  // structure = list of non-auto row names + order on both sides.
  const lastPropagatedStructureRef = useRef("");
  // Undo / Redo history for the current date's data. Snapshots are JSON
  // strings of the whole `data` object. Debounced push (700 ms) so rapid
  // typing doesn't spam the stack. Capped at 50 entries.
  const historyRef = useRef({ stack: [], pointer: -1, ignoreNext: false });
  const historyPushTimerRef = useRef(null);
  const [historyVersion, setHistoryVersion] = useState(0); // triggers button-disabled re-render

  // Reset URL when date changes
  useEffect(() => {
    if (paramDate !== dateStr) {
      navigate(`/store-paperwork/${dateStr}`, { replace: true });
    }
  }, [dateStr, paramDate, navigate]);

  // Keep the shared working date in sync so Daily Lottery opens on the same date
  useEffect(() => {
    writeSharedDate(dateStr);
  }, [dateStr]);

  // Load data for the selected date
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch the doc AND the admin's row template in parallel — we need
      // both before we can decide whether to seed a fresh doc from the
      // template (preferred) or fall back to the hardcoded defaults.
      const [dataRes, templateRes] = await Promise.all([
        axios.get(`${API}/store-paperwork/${dateStr}`),
        axios.get(`${API}/store-paperwork-template`).catch(() => ({ data: {} })),
      ]);
      const incoming = dataRes.data.data || {};
      const templateDebit = (templateRes.data?.debit_rows || [])
        .filter((r) => r && (r.name || "").trim())
        .map((r) => ({ name: r.name, amount: "" }));
      const templateCredit = (templateRes.data?.credit_rows || [])
        .filter((r) => r && (r.name || "").trim())
        .map((r) => ({ name: r.name, amount: "" }));
      // Update the template state so the reconcile useEffect (below) also
      // sees the fresh template on the same render.
      setTemplate({ debit_rows: templateDebit, credit_rows: templateCredit });

      // Migrate legacy scalar keys (z_*, n_*, s_*) into arrays on first load.
      // Once the row arrays exist, the legacy keys are ignored.
      const migrated = { ...incoming };
      // Legacy maps preserved here only to migrate any existing data.
      const LEGACY_DEBIT = [
        { key: "z_instant_1", label: "$1 Instant Lottery" }, { key: "z_instant_2", label: "$2 Instant Lottery" },
        { key: "z_instant_3", label: "$3 Instant Lottery" }, { key: "z_instant_5", label: "$5 Instant Lottery" },
        { key: "z_instant_10", label: "$10 Instant Lottery" }, { key: "z_instant_20", label: "$20 Instant Lottery" },
        { key: "z_instant_30", label: "$30 Instant Lottery" }, { key: "z_instant_50", label: "$50 Instant Lottery" },
        { key: "z_beer", label: "Beer" }, { key: "z_cig_cartons", label: "Cigarette Cartons" },
        { key: "z_cig_packs", label: "Cigarette Packs" }, { key: "z_propane_refill", label: "Propane Refill" },
        { key: "z_fee", label: "FEE" }, { key: "z_grocery_non_tax", label: "Grocery Non-Tax" },
        { key: "z_instant_paid_out", label: "Instant Paid Out" }, { key: "z_money_order", label: "Money Order" },
        { key: "z_news_paper", label: "News Paper" }, { key: "z_online_lottery_sale", label: "Online Lottery Sale" },
        { key: "z_online_paid_out", label: "Online Paid Out" }, { key: "z_other_paid_out", label: "Other Paid Out" },
        { key: "z_propane", label: "Propane" }, { key: "z_soda", label: "Soda" },
        { key: "z_taxable_grocery", label: "Taxable Grocery" }, { key: "z_tobacco", label: "Tobacco" },
      ];
      const LEGACY_CREDIT = [
        { key: "n_network_revenue", label: "Network Revenue" }, { key: "n_fuel_discounts", label: "Fuel Discounts" },
        { key: "n_first_data_total", label: "First Data Total (Machine 1)" }, { key: "n_first_data_ebt", label: "First Data EBT (Machine 1)" },
        { key: "n_coupons", label: "Coupons" }, { key: "n_total_cash", label: "Total Cash" },
        { key: "n_total_checks", label: "Total Checks" }, { key: "n_house_charges", label: "House Charges" },
        { key: "n_drive_off", label: "Drive Off" }, { key: "n_electronic_checks", label: "Electronic Checks" },
        { key: "n_roa", label: "ROA" },
      ];
      const LEGACY_SUPPLIER = [
        { key: "s_ohio_valley", label: "Ohio Valley" }, { key: "s_baumann_paper", label: "Baumann Paper" },
        { key: "s_supir_wholesale", label: "Sup. Wholesale" }, { key: "s_beer", label: "Beer" },
        { key: "s_soda", label: "Soda" }, { key: "s_all_other", label: "All Other" },
      ];
      const hydrate = (arrayKey, legacyPrefix, legacyLabels, templateRows, defaults) => {
        if (Array.isArray(migrated[arrayKey]) && migrated[arrayKey].length > 0) return;
        // Try to pull values from legacy keys; if none exist, seed from
        // template (preferred) or fall back to hardcoded defaults.
        const legacyKeys = Object.keys(migrated).filter((k) => k.startsWith(legacyPrefix));
        if (legacyKeys.length > 0) {
          migrated[arrayKey] = defaults.map((d) => {
            // best-effort mapping: find a legacy key whose label matches
            const found = legacyLabels.find((lk) => lk.label === d.name);
            return { name: d.name, amount: found ? (migrated[found.key] || "") : "" };
          });
          // Drop the old scalar keys so we don't double-store.
          legacyKeys.forEach((k) => { delete migrated[k]; });
        } else {
          // Fresh doc: prefer the admin template (users see the same layout
          // the admin has defined). Fall back to defaults only when the
          // template is empty (brand-new deployment).
          const seed = (Array.isArray(templateRows) && templateRows.length > 0)
            ? templateRows
            : defaults;
          migrated[arrayKey] = seed.map((d) => ({ name: d.name, amount: "" }));
        }
      };
      hydrate("debit_rows", "z_", LEGACY_DEBIT, templateDebit, DEFAULT_DEBIT_ROWS);
      hydrate("credit_rows", "n_", LEGACY_CREDIT, templateCredit, DEFAULT_CREDIT_ROWS);
      hydrate("supplier_rows", "s_", LEGACY_SUPPLIER, null, DEFAULT_SUPPLIER_ROWS);
      // Fuel rows: no legacy migration needed (brand-new section). Seed the
      // default 5 grades when the doc doesn't have any yet.
      if (!Array.isArray(migrated.fuel_rows) || migrated.fuel_rows.length === 0) {
        migrated.fuel_rows = DEFAULT_FUEL_ROWS.map((r) => ({ ...r }));
      }
      if (!Array.isArray(migrated.cash_paid_out)) migrated.cash_paid_out = [];

      // Draft restore: prefer a local draft newer than the server copy
      // (recovers edits lost to tab suspension / crash before auto-save fired).
      let restoredDraft = false;
      try {
        const raw = localStorage.getItem(`sp_draft_${dateStr}`);
        if (raw) {
          const draft = JSON.parse(raw);
          const serverTs = dataRes.data.updated_at ? Date.parse(dataRes.data.updated_at) : 0;
          if (draft?.date === dateStr && Number(draft.ts) > serverTs && draft.data) {
            setData(draft.data);
            // Seed lastSaved to the SERVER copy so the pending auto-save sees
            // the draft as unsaved and actually persists it.
            lastSavedSnapshotRef.current = JSON.stringify(migrated);
            lastPropagatedStructureRef.current = _rowStructureKey(draft.data);
            isFirstLoadRef.current = false;
            restoredDraft = true;
            toast.message("Restored unsaved Store Paperwork changes from your last session — saving now…");
          } else {
            localStorage.removeItem(`sp_draft_${dateStr}`);
          }
        }
      } catch { /* localStorage unavailable — ignore */ }

      if (!restoredDraft) {
        setData(migrated);
        isFirstLoadRef.current = true; // suppress next auto-save
      }
    } catch (err) {
      toast.error("Failed to load store paperwork");
    } finally {
      setLoading(false);
    }
  }, [dateStr]);
  useEffect(() => { loadData(); }, [loadData]);

  // Load tax settings (per-user override + admin defaults).
  const loadTaxSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/store-paperwork-tax-settings`);
      setTaxSettings(res.data);
    } catch (err) {
      // Silent fallback to hard defaults — already in state.
    }
  }, []);
  useEffect(() => { loadTaxSettings(); }, [loadTaxSettings]);

  // Load per-denomination lottery sale $ from Daily Entry for this date.
  // Any Debit row named "$N Instant Lottery" gets its amount force-set
  // read-only from this fetch (see the applyAutoLotteryRows effect below).
  const loadLotterySales = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lottery-sales-by-denom/${dateStr}`);
      // Store the full response so applyAutoLotteryRows can access per_denom
      // AND the named rollups (online_sale, instant_paid_out, online_paid_out).
      setLotterySales(res.data || {});
    } catch {
      // If there's no daily-entry doc for this date, leave the amounts blank
      // (users can still see the row list but the auto-fill will be empty).
      setLotterySales({});
    }
  }, [dateStr]);
  useEffect(() => { loadLotterySales(); }, [loadLotterySales]);


  // Vendor master list (per user) — used by the Supplier Section dropdown.
  const loadVendors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/store-paperwork-vendors`);
      setVendors(res.data?.vendors || []);
    } catch {
      setVendors([]);
    }
  }, []);
  useEffect(() => { loadVendors(); }, [loadVendors]);

  const addVendor = useCallback(async (name) => {
    const clean = (name || "").trim();
    if (!clean) return null;
    try {
      const res = await axios.post(`${API}/store-paperwork-vendors`, { name: clean });
      const v = res.data?.vendor;
      if (res.data?.duplicated) {
        toast.info(`Vendor "${v.name}" already exists`);
      } else {
        toast.success(`Vendor "${v.name}" added`);
      }
      await loadVendors();
      return v;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add vendor");
      return null;
    }
  }, [loadVendors]);

  const renameVendor = useCallback(async (vendorId, name) => {
    const clean = (name || "").trim();
    if (!clean) return;
    try {
      await axios.put(`${API}/store-paperwork-vendors/${vendorId}`, { name: clean });
      await loadVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to rename vendor");
    }
  }, [loadVendors]);

  const deleteVendor = useCallback(async (vendorId) => {
    try {
      await axios.delete(`${API}/store-paperwork-vendors/${vendorId}`);
      await loadVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete vendor");
    }
  }, [loadVendors]);



  // Load the admin-curated Debit/Credit row template (any auth user).
  const loadTemplate = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/store-paperwork-template`);
      setTemplate({
        debit_rows: res.data?.debit_rows || [],
        credit_rows: res.data?.credit_rows || [],
      });
    } catch {
      // Silent — template is optional, fall back to empty list
    }
  }, []);
  useEffect(() => { loadTemplate(); }, [loadTemplate]);

  // Reconcile data.debit_rows / credit_rows with the template for non-admins.
  // Returns the SAME reference when nothing changes, so we can run this effect
  // safely on every data/template change.
  useEffect(() => {
    if (loading) return;
    if (isAdmin) return; // Admins keep their full add/delete freedom
    // Build a combined name→amount map from BOTH the user's Debit AND Credit
    // rows. This lets amounts follow a row when the admin moves it across
    // sides (Debit ↔ Credit) — the user's number for "Money Order" doesn't
    // get lost just because it's now classified as a Credit item.
    const collectAmounts = (...lists) => {
      const acc = new Map();
      for (const list of lists) {
        if (!Array.isArray(list)) continue;
        for (const r of list) {
          if (!r || r._kind) continue;
          const key = (r.name || "").trim().toLowerCase();
          if (!key || acc.has(key)) continue;
          acc.set(key, r.amount || "");
        }
      }
      return acc;
    };
    const amountByName = collectAmounts(data.debit_rows, data.credit_rows);
    const reconcile = (current, tpl, allowedAutoKinds) => {
      const safe = Array.isArray(current) ? current : [];
      const base = safe.filter((r) => r && !r._kind);
      const autoRows = safe.filter((r) => r && allowedAutoKinds.has(r._kind));
      const tplList = (tpl || []).filter((t) => t && (t.name || "").trim());
      const templateKeySet = new Set(tplList.map((t) => (t.name || "").trim().toLowerCase()));
      // Template rows FIRST (in template order), amounts merged from user's data.
      const templateRows = tplList.map((t) => ({
        name: t.name,
        amount: amountByName.get((t.name || "").trim().toLowerCase()) || "",
      }));
      // User's custom rows: any non-auto row in `current` whose name is NOT
      // in the template. Preserve their original order. This lets non-admin
      // users add their own extra rows on top of the admin-controlled
      // template — those rows survive template changes as long as their
      // names don't collide.
      const customRows = base.filter((r) => {
        const k = (r?.name || "").trim().toLowerCase();
        if (!k) return true; // blank-name rows the user just added — keep them so they can name it
        return !templateKeySet.has(k);
      });
      const reconciledBase = [...templateRows, ...customRows];
      // Check if anything changed before returning a new array
      if (reconciledBase.length === base.length) {
        let same = true;
        for (let i = 0; i < reconciledBase.length; i++) {
          if (
            reconciledBase[i].name !== base[i].name ||
            String(reconciledBase[i].amount || "") !== String(base[i].amount || "")
          ) {
            same = false;
            break;
          }
        }
        if (same) return current; // no change
      }
      return [...reconciledBase, ...autoRows];
    };
    // Debit-side auto-rows: Total Fuel + State Tax + City Beer Tax pinned at bottom.
    // Credit-side auto-rows: Vendors Cash Paid Out pinned at the bottom.
    const debitAutoKinds = new Set([STATE_TAX_KIND, BEER_TAX_KIND, TOTAL_FUEL_KIND]);
    const creditAutoKinds = new Set([VENDORS_CASH_KIND]);
    const nextDebit = reconcile(data.debit_rows, template.debit_rows, debitAutoKinds);
    const nextCredit = reconcile(data.credit_rows, template.credit_rows, creditAutoKinds);
    if (nextDebit !== data.debit_rows || nextCredit !== data.credit_rows) {
      setData((d) => ({ ...d, debit_rows: nextDebit, credit_rows: nextCredit }));
    }
  }, [isAdmin, loading, template.debit_rows, template.credit_rows, data.debit_rows, data.credit_rows]);

  // Cash-method supplier total — sum of data.supplier_rows[i].amount where
  // method === 'cash'. Also treats rows with a missing/blank method as "cash"
  // because that's what the Supplier UI displays as the default before the
  // user explicitly picks something. Without this, rows added via legacy
  // paths (or new rows where the user just types an amount without touching
  // the method dropdown) silently drop out of the roll-up and the "Vendors
  // Cash Paid Out" pinned debit row stays at $0. Feeds `applyAutoTaxRows`.
  const supplierCashTotal = useMemo(() => {
    const rows = Array.isArray(data.supplier_rows) ? data.supplier_rows : [];
    return rows.reduce((s, r) => {
      if (!r) return s;
      const m = (r.method || "cash").toLowerCase();
      return m === "cash" ? s + num(r.amount) : s;
    }, 0);
  }, [data.supplier_rows]);

  // Total fuel sold amount = Σ fuel_rows[i].sold_amount. Feeds the pinned
  // read-only "Total Fuel" debit row via applyAutoTaxRows.
  const totalFuelSoldAmount = useMemo(() => {
    const rows = Array.isArray(data.fuel_rows) ? data.fuel_rows : [];
    return rows.reduce((s, r) => s + num(r?.sold_amount), 0);
  }, [data.fuel_rows]);

  // Whenever debit rows or tax % change, refresh the pinned bottom auto-rows
  // on Debit (Total Fuel → State Tax → City Beer Tax). applyAutoTaxRows
  // returns SAME reference when nothing changes → no loop.
  useEffect(() => {
    if (loading) return;
    const current = data.debit_rows;
    const next = applyAutoTaxRows(current, taxSettings.state_tax_percent, taxSettings.beer_tax_percent, totalFuelSoldAmount);
    if (next !== current) {
      setData((d) => ({ ...d, debit_rows: next }));
    }
  }, [data.debit_rows, taxSettings.state_tax_percent, taxSettings.beer_tax_percent, totalFuelSoldAmount, loading]);

  // Whenever credit rows or the supplier cash total change, pin the
  // "Vendors Cash Paid Out" auto-row at the end of the Credit list. Paid-outs
  // (cash leaving the register) live on the Credit side alongside Instant /
  // Online / Other Paid Out. Returns SAME reference when nothing changes → no loop.
  useEffect(() => {
    if (loading) return;
    const current = data.credit_rows;
    const next = applyAutoVendorsCashCreditRow(current, supplierCashTotal);
    if (next !== current) {
      setData((d) => ({ ...d, credit_rows: next }));
    }
  }, [data.credit_rows, supplierCashTotal, loading]);

  // Whenever debit/credit rows or lottery-sale fetch settle, force-fill the
  // lottery-auto rows ("$N Instant Lottery", "Online Lottery Sale",
  // "Instant Paid Out", "Online Paid Out") with Daily-Entry's numbers.
  // Applies to BOTH sections because admin templates often put paid-outs on
  // the Credit side. applyAutoLotteryRows returns SAME reference when nothing
  // changes → no loop.
  useEffect(() => {
    if (loading) return;
    if (!lotterySales) return;
    const nextDebit = applyAutoLotteryRows(data.debit_rows, lotterySales);
    const nextCredit = applyAutoLotteryRows(data.credit_rows, lotterySales);
    if (nextDebit !== data.debit_rows || nextCredit !== data.credit_rows) {
      setData((d) => ({ ...d, debit_rows: nextDebit, credit_rows: nextCredit }));
    }
  }, [data.debit_rows, data.credit_rows, lotterySales, loading]);

  // Debounced auto-save -----------------------------------------------------
  // We keep saveNow itself a `useCallback` for the explicit "Save" button (so
  // it gets a fresh closure over `data` each render), but the auto-save
  // useEffect uses saveNowRef.current — that way the effect only re-runs when
  // `data` actually changes, NOT when saveNow's identity changes. Without
  // this, the save button used to flash and saves sometimes dropped under
  // races because every successful save would update `data` (via setData(res.data.data))
  // which would rebuild saveNow → re-fire the effect → schedule another save.
  const saveNow = useCallback(async (silent = true) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    // Skip if nothing changed since the last successful save
    const snapshot = JSON.stringify(data);
    if (snapshot === lastSavedSnapshotRef.current) {
      setSaveStatus((s) => (s === "saving" ? "saved" : s));
      return;
    }
    setSaveStatus("saving");
    try {
      const res = await axios.put(`${API}/store-paperwork/${dateStr}`, { data });
      const returned = res.data?.data;
      // Only setData(returned) if the server actually returned something
      // STRUCTURALLY different (e.g. enriched fields). If it matches the
      // payload we sent, skip the setData to prevent a re-render loop.
      if (returned && JSON.stringify(returned) !== snapshot) {
        setData(returned);
        lastSavedSnapshotRef.current = JSON.stringify(returned);
      } else {
        lastSavedSnapshotRef.current = snapshot;
      }
      setSaveStatus("saved");
      setDataRefreshKey((k) => k + 1);
      // Server confirmed — safe to drop the local draft.
      try { localStorage.removeItem(`sp_draft_${dateStr}`); } catch { /* ignore */ }
      if (!silent) toast.success("Saved");
      setTimeout(() => setSaveStatus((s) => (s === "saved" ? "idle" : s)), 1500);
    } catch (err) {
      setSaveStatus("error");
      toast.error("Save failed");
    }
  }, [data, dateStr]);
  // Keep ref pointing at latest closure so the effect can call it without
  // including it in deps.
  saveNowRef.current = saveNow;

  useEffect(() => {
    if (loading) return;
    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
      // Seed the "lastSaved" snapshot so we don't trigger an immediate save
      // right after a load.
      lastSavedSnapshotRef.current = JSON.stringify(data);
      // Seed the propagation snapshot too so opening a date doesn't push
      // its layout everywhere on load.
      lastPropagatedStructureRef.current = _rowStructureKey(data);
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      await saveNowRef.current(true);
      // Admin: after a successful auto-save, if the ROW STRUCTURE changed
      // (row names/order/count on either side — NOT just amount edits),
      // propagate the layout to (a) every other date the admin owns and
      // (b) the shared template that non-admin users follow. This makes
      // any add/delete/rename on any date auto-apply everywhere without
      // the admin having to click the toolbar Save button.
      if (isAdmin) {
        const structKey = _rowStructureKey(data);
        if (structKey && structKey !== lastPropagatedStructureRef.current) {
          lastPropagatedStructureRef.current = structKey;
          try {
            await Promise.all([
              syncLayoutToAllDatesRef.current({ silent: true }),
              pushLayoutToSharedTemplateRef.current(),
            ]);
          } catch {
            // non-fatal — the base save already succeeded
          }
        }
      }
    }, 900);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [data, loading, isAdmin]);

  // Persist an in-progress draft to localStorage whenever there are unsaved
  // changes — last-line-of-defense against tab suspension / crash. Restored
  // by loadData if newer than the server copy; cleared after a saved PUT.
  useEffect(() => {
    if (loading || isFirstLoadRef.current) return;
    const snapshot = JSON.stringify(data);
    if (snapshot === lastSavedSnapshotRef.current) return; // nothing unsaved
    try {
      localStorage.setItem(`sp_draft_${dateStr}`, JSON.stringify({ ts: Date.now(), date: dateStr, data }));
    } catch { /* localStorage full or unavailable — silent */ }
  }, [data, loading, dateStr]);

  // Flush unsaved changes via keepalive fetch the moment the tab is hidden —
  // background tabs throttle setTimeout so the debounced save may never fire.
  const spFlushRef = useRef(null);
  spFlushRef.current = () => {
    if (loading) return;
    const snapshot = JSON.stringify(data);
    if (snapshot === lastSavedSnapshotRef.current) return;
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    keepaliveSave(`${API}/store-paperwork/${dateStr}`, { data });
    lastSavedSnapshotRef.current = snapshot;
  };
  useFlushOnHide(spFlushRef);

  // Undo/Redo history: push a JSON snapshot of `data` 700ms after any
  // change. `ignoreNext` prevents undo/redo actions themselves from adding a
  // new history entry (they set data to a snapshot that IS a history state).
  useEffect(() => {
    if (loading || isFirstLoadRef.current) return;
    if (historyRef.current.ignoreNext) {
      historyRef.current.ignoreNext = false;
      return;
    }
    if (historyPushTimerRef.current) clearTimeout(historyPushTimerRef.current);
    historyPushTimerRef.current = setTimeout(() => {
      const snap = JSON.stringify(data);
      const h = historyRef.current;
      if (h.stack[h.pointer] === snap) return;
      // Truncate any redo history when the user branches off a mid-history state
      h.stack = h.stack.slice(0, h.pointer + 1);
      h.stack.push(snap);
      if (h.stack.length > 50) h.stack.shift();
      h.pointer = h.stack.length - 1;
      setHistoryVersion((v) => v + 1);
    }, 700);
    return () => { if (historyPushTimerRef.current) clearTimeout(historyPushTimerRef.current); };
  }, [data, loading]);

  const canUndo = historyRef.current.pointer > 0;
  const canRedo = historyRef.current.pointer < historyRef.current.stack.length - 1;

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (h.pointer <= 0) return;
    h.pointer -= 1;
    h.ignoreNext = true;
    setData(JSON.parse(h.stack[h.pointer]));
    setHistoryVersion((v) => v + 1);
    toast.success("Undo");
  }, []);

  const redo = useCallback(() => {
    const h = historyRef.current;
    if (h.pointer >= h.stack.length - 1) return;
    h.pointer += 1;
    h.ignoreNext = true;
    setData(JSON.parse(h.stack[h.pointer]));
    setHistoryVersion((v) => v + 1);
    toast.success("Redo");
  }, []);

  const clearPage = useCallback(() => {
    const ok = window.confirm(
      "Clear this page?\n\nAll dollar amounts on the Debit, Credit, Supplier and Fuel sections for this date will be wiped. Row layout is kept.\n\nYou can Undo right after."
    );
    if (!ok) return;
    setData((d) => {
      const blankRows = (rows) => (Array.isArray(rows) ? rows : []).map((r) => (r?._kind ? { ...r } : { ...r, amount: "" }));
      const blankSupplierRows = (rows) => (Array.isArray(rows) ? rows : []).map((r) => ({ ...r, amount: "" }));
      const blankFuelRows = (rows) => (Array.isArray(rows) ? rows : []).map((r) => ({
        ...r, sold_volume: "", sold_amount: "", sign_price: "", cost_price: "", in_tank_volume: "", fuel_delivery: "",
      }));
      return {
        ...d,
        debit_rows: blankRows(d.debit_rows),
        credit_rows: blankRows(d.credit_rows),
        supplier_rows: blankSupplierRows(d.supplier_rows),
        fuel_rows: blankFuelRows(d.fuel_rows),
        cash_paid_out: [],
      };
    });
    toast.success("Page cleared — click Undo to revert");
  }, []);

  // Keyboard shortcuts: Ctrl/Cmd + Z = Undo, Ctrl/Cmd + Shift + Z (or Ctrl+Y) = Redo.
  useEffect(() => {
    const onKey = (e) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (!isMod) return;
      const key = e.key.toLowerCase();
      // Skip if user is typing in an input and using arrow/text keys (only trap Z/Y)
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  // Field updater
  const update = (key, value) => setData((d) => ({ ...d, [key]: value }));

  // Admin: sync THIS date's row layout (names + order) to every other saved
  // date. Amounts on each target date are preserved by name-match; new rows
  // start empty; rows no longer in this date's layout are removed from the
  // target. Auto rows (State Tax, Beer Tax, Vendors Cash) are recomputed by
  // the frontend effects — nothing to worry about.
  const [syncingLayout, setSyncingLayout] = useState(false);
  // Passive status pill next to the Save button — "synced" | "syncing" |
  // "error". Flips to "syncing" as soon as any sync starts (manual or auto),
  // back to "synced" on success, "error" on network / permission failure.
  const [syncStatus, setSyncStatus] = useState("synced");
  const syncLayoutToAllDates = useCallback(async ({ silent = false } = {}) => {
    if (!isAdmin) return null;
    if (syncingLayout) return null;
    setSyncingLayout(true);
    setSyncStatus("syncing");
    try {
      const res = await axios.post(`${API}/admin/store-paperwork/sync-layout-from-date/${dateStr}`);
      const { docs_updated, docs_scanned, dates_updated } = res.data || {};
      if (!silent) {
        if (docs_updated > 0) {
          toast.success(`Layout synced to ${docs_updated}/${docs_scanned} other date${docs_scanned === 1 ? "" : "s"}.`);
        } else {
          toast.info("All other dates already match this layout.");
        }
      }
      console.log("Sync layout — dates updated:", dates_updated);
      setSyncStatus("synced");
      return res.data;
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || "Sync layout failed");
      setSyncStatus("error");
      return null;
    } finally {
      setSyncingLayout(false);
    }
  }, [isAdmin, dateStr, syncingLayout]);

  // Admin: push the current date's row NAMES (Debit + Credit, non-auto) to
  // the shared store_paperwork_template so every non-admin user sees this
  // layout as the standard rows on their Store Paperwork. Non-admin users
  // still get to add their own custom rows alongside the template.
  const pushLayoutToSharedTemplate = useCallback(async () => {
    if (!isAdmin) return null;
    const asTplRows = (rows) => (Array.isArray(rows) ? rows : [])
      .filter((r) => r && !r._kind)
      .map((r) => ({ name: (r.name || "").trim() }))
      .filter((r) => r.name);
    const debit_rows = asTplRows(data.debit_rows);
    const credit_rows = asTplRows(data.credit_rows);
    try {
      const res = await axios.put(`${API}/admin/store-paperwork-template`, { debit_rows, credit_rows });
      // Refresh local template so admin sees the sync landed (also keeps
      // any downstream memoized template consumers consistent).
      setTemplate({
        debit_rows: res.data?.debit_rows || [],
        credit_rows: res.data?.credit_rows || [],
      });
      return res.data;
    } catch {
      // Non-fatal — the save itself already succeeded.
      return null;
    }
  }, [isAdmin, data.debit_rows, data.credit_rows]);

  // Wraps the toolbar "Save" click for admins: save this date first, then
  // (1) propagate the row layout to every other date the admin owns, and
  // (2) push the layout to the shared template so every non-admin user
  // sees the same rows on their Store Paperwork. Regular users just save.
  const saveAndMaybeSyncLayout = useCallback(async () => {
    await saveNow(false);
    if (isAdmin) {
      await syncLayoutToAllDates({ silent: true });
      await pushLayoutToSharedTemplate();
      // Mark the propagated snapshot so the auto-save side-effect doesn't
      // re-fire the same sync a moment later.
      lastPropagatedStructureRef.current = _rowStructureKey(data);
    }
  }, [saveNow, isAdmin, syncLayoutToAllDates, pushLayoutToSharedTemplate, data]);

  // Keep refs pointing at the latest helpers so the auto-save useEffect can
  // call them without listing them as deps (which would re-fire the effect
  // on every render).
  syncLayoutToAllDatesRef.current = syncLayoutToAllDates;
  pushLayoutToSharedTemplateRef.current = pushLayoutToSharedTemplate;

  // Bulk fix: sweeps every past paperwork doc belonging to this admin.
  const [bulkFixing, setBulkFixing] = useState(false);
  const bulkFixAllLayouts = async () => {
    if (bulkFixing) return;
    const ok = window.confirm(
      "Reorganize the layout of EVERY past Store Paperwork entry you saved?\n\n" +
      "• Moves all \"…Paid Out\" rows from Debit → Credit\n" +
      "• Groups all lottery rows at the top of Debit\n" +
      "• Amounts are preserved; nothing is deleted\n\n" +
      "This runs across ALL your saved dates in one pass."
    );
    if (!ok) return;
    setBulkFixing(true);
    try {
      const res = await axios.post(`${API}/admin/store-paperwork/fix-layout-all`);
      const { docs_scanned, docs_updated, total_paid_out_moved, total_lottery_grouped, dates_updated } = res.data || {};
      toast.success(
        `Bulk fix complete: ${docs_updated}/${docs_scanned} dates updated — moved ${total_paid_out_moved || 0} paid-out row(s), grouped ${total_lottery_grouped || 0} lottery row(s). Reloading current date…`
      );
      // Refresh the current date so the admin sees their own doc's new layout.
      await loadData();
      console.log("Fix Layout — dates updated:", dates_updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk fix failed");
    } finally {
      setBulkFixing(false);
    }
  };

  // Admin-only: same as bulkFixAllLayouts but sweeps EVERY user in the
  // database (not just the requesting admin). Used to migrate legacy layouts
  // across the whole tenant — e.g. after we rolled out the "collapsed
  // Instant Lottery" and "Total Fuel auto row" changes. Optionally accepts
  // a list of row names to fully purge from every doc (both sides).
  const [bulkFixingAllUsers, setBulkFixingAllUsers] = useState(false);
  const bulkFixAllUsers = async () => {
    if (bulkFixingAllUsers) return;
    // Ask if the admin wants to purge specific row names on top of the standard fix.
    const purgeInput = window.prompt(
      "Fix Store Paperwork layout for ALL USERS on ALL dates.\n\n" +
      "Optional: enter row names to PURGE (one per line, case-insensitive SUBSTRING match).\n" +
      "Leave blank to skip. Examples:\n" +
      "  First Data     ← deletes 'First Data Total', 'First Data Total (Machine 1)', 'First Data EBT (Machine 1)', etc.\n" +
      "  Money Order    ← deletes any row whose name contains 'Money Order'\n\n" +
      "The standard fix already dedupes name variants (e.g. \"Beer\" merges into \"Beer (T)\").",
      ""
    );
    // window.prompt returns null on Cancel — abort entirely then.
    if (purgeInput === null) return;
    const purgeNames = purgeInput
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    const summary =
      "About to run global fix:\n" +
      "• Collapses legacy $1–$50 Instant Lottery rows into one \"Instant Lottery\"\n" +
      "• Moves \"…Paid Out\" rows from Debit → Credit\n" +
      "• Groups lottery rows at top of Debit\n" +
      "• Drops manual duplicates of auto rows\n" +
      "• Merges duplicate rows by name (incl. parens variants like \"Beer\" ↔ \"Beer (T)\")\n" +
      (purgeNames.length ? `• PURGES these row names: ${purgeNames.join(", ")}\n` : "") +
      "\nAmounts are preserved (summed when merging). Proceed?";
    if (!window.confirm(summary)) return;

    setBulkFixingAllUsers(true);
    try {
      const res = await axios.post(
        `${API}/admin/store-paperwork/fix-layout-all-users`,
        purgeNames.length ? { purge_names: purgeNames } : {}
      );
      const {
        docs_scanned = 0,
        docs_updated = 0,
        users_touched = 0,
        dates_touched = 0,
        total_paid_out_moved = 0,
        total_lottery_grouped = 0,
        total_per_denom_collapsed = 0,
        total_deduped = 0,
        total_purged = 0,
      } = res.data || {};
      toast.success(
        `Global fix complete — ${docs_updated}/${docs_scanned} docs across ${users_touched} user(s), ${dates_touched} date(s). ` +
        `Collapsed ${total_per_denom_collapsed} legacy denom row(s), moved ${total_paid_out_moved} paid-out(s), grouped ${total_lottery_grouped} lottery row(s), ` +
        `merged ${total_deduped} duplicate(s)` +
        (total_purged ? `, PURGED ${total_purged} row(s).` : "."),
        { duration: 10000 }
      );
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Global fix failed");
    } finally {
      setBulkFixingAllUsers(false);
    }
  };

  // Admin-only: one-click "Fix Layout" for their own daily entry.
  // - Moves any user-editable "…Paid Out" row from data.debit_rows into
  //   data.credit_rows (preserving amount, deduping by name).
  // - Reorders data.debit_rows so all lottery-auto rows ($1-$50 Instant
  //   Lottery + Online Lottery Sale) sit together at the TOP, followed by
  //   everything else, with State Tax + City Beer Tax auto-rows still pinned
  //   at the bottom (applyAutoTaxRows will re-run and re-pin regardless).
  // - Vendors Cash Paid Out auto-row is already on Credit (see the
  //   applyAutoVendorsCashCreditRow effect) so nothing to move there.
  const fixLayoutForAdmin = () => {
    const LOTTERY_ORDER = [
      "Instant Lottery",
      "$1 Instant Lottery", "$2 Instant Lottery", "$3 Instant Lottery", "$5 Instant Lottery",
      "$10 Instant Lottery", "$20 Instant Lottery", "$30 Instant Lottery", "$50 Instant Lottery",
      "Online Lottery Sale",
    ];
    const lotteryKey = (name) => (name || "").trim().toLowerCase();
    const lotterySet = new Set(LOTTERY_ORDER.map(lotteryKey));
    const isPaidOut = (name) => {
      const n = (name || "").toLowerCase().replace(/[\s-]+/g, " ").trim();
      return n.includes("paid out");
    };
    // Detect the 8 legacy per-denom rows ("$1 Instant Lottery" … "$50 Instant
    // Lottery") vs the new aggregate row ("Instant Lottery"). All per-denom
    // rows get collapsed into ONE aggregate row (amount auto-fills from
    // Daily Entry's summed instant_sale).
    const RE_INSTANT_DENOM = /^\$?\s*\d+\s+instant\s+lottery\b/i;
    const RE_INSTANT_AGGREGATE = /^\s*instant\s+lottery\s*$/i;

    const currentDebit = Array.isArray(data.debit_rows) ? data.debit_rows : [];
    const currentCredit = Array.isArray(data.credit_rows) ? data.credit_rows : [];

    const movedPaidOut = [];
    const lotteryRows = [];
    const otherRows = [];
    const debitAutoRows = [];
    let collapsedDenoms = 0;
    let hasAggregate = false;
    for (const r of currentDebit) {
      if (!r) continue;
      if (r._kind === STATE_TAX_KIND || r._kind === BEER_TAX_KIND || r._kind === TOTAL_FUEL_KIND) { debitAutoRows.push(r); continue; }
      if (r._kind) continue;
      if (isPaidOut(r.name)) { movedPaidOut.push(r); continue; }
      // Collapse per-denom → single aggregate.
      if (RE_INSTANT_DENOM.test(r.name || "")) { collapsedDenoms++; continue; }
      if (RE_INSTANT_AGGREGATE.test(r.name || "")) { hasAggregate = true; lotteryRows.push(r); continue; }
      if (lotterySet.has(lotteryKey(r.name))) { lotteryRows.push(r); continue; }
      otherRows.push(r);
    }
    if (collapsedDenoms > 0 && !hasAggregate) {
      lotteryRows.push({ name: "Instant Lottery", amount: "" });
    }
    lotteryRows.sort((a, b) => LOTTERY_ORDER.findIndex((n) => lotteryKey(n) === lotteryKey(a.name)) - LOTTERY_ORDER.findIndex((n) => lotteryKey(n) === lotteryKey(b.name)));

    const creditNames = new Set(currentCredit.filter((r) => r && !r._kind).map((r) => (r.name || "").trim().toLowerCase()));
    const additions = movedPaidOut.filter((r) => !creditNames.has((r.name || "").trim().toLowerCase()));
    const creditBase = currentCredit.filter((r) => r && !r._kind);
    const creditAuto = currentCredit.filter((r) => r && r._kind);
    const nextCredit = [...creditBase, ...additions, ...creditAuto];

    const nextDebit = [...lotteryRows, ...otherRows, ...debitAutoRows];
    setData((d) => ({ ...d, debit_rows: nextDebit, credit_rows: nextCredit }));

    const parts = [];
    if (additions.length) parts.push(`moved ${additions.length} Paid-Out row${additions.length === 1 ? "" : "s"} to Credit`);
    if (collapsedDenoms > 0) parts.push(`collapsed ${collapsedDenoms} $N Instant Lottery row${collapsedDenoms === 1 ? "" : "s"} → single "Instant Lottery"`);
    if (lotteryRows.length && collapsedDenoms === 0) parts.push(`grouped ${lotteryRows.length} lottery row${lotteryRows.length === 1 ? "" : "s"} on Debit`);
    toast.success(parts.length ? `Layout fixed: ${parts.join(", ")}.` : "Layout already tidy.");
  };

  // Computed totals --------------------------------------------------------
  const debitRows = data.debit_rows || [];
  const creditRows = data.credit_rows || [];
  const supplierRows = data.supplier_rows || [];
  const cashPaidOutRows = data.cash_paid_out || [];

  const debitTotal = useMemo(() => debitRows.reduce((s, r) => s + num(r.amount), 0), [debitRows]);
  const creditTotal = useMemo(() => creditRows.reduce((s, r) => s + num(r.amount), 0), [creditRows]);
  // Suppliers are ACH accounts and DO NOT affect Daily Over/Short — they're shown for tracking only.
  const supplierTotal = useMemo(() => supplierRows.reduce((s, r) => s + num(r.amount), 0), [supplierRows]);
  const cashPaidOutTotal = useMemo(() => cashPaidOutRows.reduce((s, r) => s + num(r.amount), 0), [cashPaidOutRows]);

  // Inside Sales = sum of Debit rows EXCEPT lottery / fuel / money-order / tax
  // auto-rows. Represents actual in-store merchandise sales (Beer, Cigarettes,
  // Grocery, etc.). Everything already flagged as a `_kind` auto row (State
  // Tax, City Beer Tax, Total Fuel) is skipped automatically. Lottery rows
  // are detected via detectLotteryDenom; Money Order is a name match.
  const insideSalesTotal = useMemo(() => {
    return debitRows.reduce((s, r) => {
      if (!r || r._kind) return s;
      const name = (r.name || "").trim();
      if (!name) return s;
      if (detectLotteryDenom(name) != null) return s;
      if (name.toLowerCase() === "money order") return s;
      return s + num(r.amount);
    }, 0);
  }, [debitRows]);

  // Bank Deposit = "Total Cash" + "Total Checks" only (Electronic Checks and
  // other tenders explicitly excluded per user spec).
  const bankDepositTotal = useMemo(() => {
    return creditRows.reduce((s, r) => {
      if (!r || r._kind) return s;
      const key = (r.name || "").trim().toLowerCase();
      if (key === "total cash" || key === "total checks") return s + num(r.amount);
      return s;
    }, 0);
  }, [creditRows]);

  // Taxable departments total = Σ rows suffixed "(T)" or "(Beer T)".
  const taxableDeptTotal = useMemo(() => {
    return debitRows.reduce((s, r) => {
      if (!r || r._kind) return s;
      const cls = classifyTaxableRow(r.name);
      if (cls === TAX_STATE_TAXABLE || cls === TAX_BEER_TAXABLE) return s + num(r.amount);
      return s;
    }, 0);
  }, [debitRows]);

  // Combined tax total = State Tax auto row + City Beer Tax auto row.
  const taxTotal = useMemo(() => {
    return debitRows.reduce((s, r) => {
      if (r && (r._kind === STATE_TAX_KIND || r._kind === BEER_TAX_KIND)) return s + num(r.amount);
      return s;
    }, 0);
  }, [debitRows]);

  // Helper: find a row's amount by name (used for Inside Sale).
  const rowAmt = (rows, name) => num((rows.find((r) => (r.name || "").trim().toLowerCase() === name.toLowerCase()) || {}).amount);

  // Fuel totals
  const fuelTotals = useMemo(() => {
    let vol = 0, amt = 0, veeder = 0, epa = 0;
    FUEL_GRADES.forEach((g) => {
      vol += num(data[`f_${g.key}_volume`]);
      amt += num(data[`f_${g.key}_amount`]);
      veeder += num(data[`v_${g.key}`]);
      epa += num(data[`epa_${g.key}`]);
    });
    return { vol, amt, veeder, epa };
  }, [data]);

  // Lottery section (auto-pulled, read-only)
  const totalLottery = num(data.l_instant_sale) + num(data.l_online_sale);

  // Daily Sale summary (computed). Inside Sale = Network Revenue minus cash-equivalents
  // captured on the Credit side. Suppliers (ACH) are intentionally excluded.
  const gasGallons = fuelTotals.vol;
  const insideSale = rowAmt(creditRows, "Network Revenue")
    - rowAmt(creditRows, "Total Cash")
    - rowAmt(creditRows, "Total Checks")
    - rowAmt(creditRows, "House Charges")
    - rowAmt(creditRows, "Drive Off")
    - rowAmt(creditRows, "Electronic Checks")
    - rowAmt(creditRows, "ROA");
  const shiftOver = num(data.ds_shift_over); // user-typed
  const overShort = creditTotal - debitTotal;

  // Navigation -------------------------------------------------------------
  // Warn when switching FROM a recent date TO a non-recent one (old or future)
  const [pendingDate, setPendingDate] = useState(null);
  const requestDateChange = (d) => {
    if (!d) return;
    if (isNotRecentDay(d) && !isNotRecentDay(selectedDate)) setPendingDate(d);
    else setSelectedDate(d);
  };
  const goPrev = () => requestDateChange(subDays(selectedDate, 1));
  const goNext = () => requestDateChange(addDays(selectedDate, 1));

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4 bg-emerald-800 p-4 rounded-xl"
      data-testid="store-paperwork-page"
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <div className="flex items-center gap-2 mr-2">
          <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <h1 className="font-mono text-2xl font-bold tracking-tight" data-testid="sp-title">Store Paperwork</h1>
        </div>

        <Button variant="outline" size="icon" onClick={goPrev} className="btn-3d" data-testid="sp-prev-day">
          <ChevronLeft className="w-4 h-4" />
        </Button>

        <DateWithDataPicker
          endpoint="/store-paperwork/dates/range"
          selectedDate={selectedDate}
          onSelect={requestDateChange}
          testId="sp-date"
          refreshKey={dataRefreshKey}
        />

        <Button variant="outline" size="icon" onClick={goNext} className="btn-3d" data-testid="sp-next-day">
          <ChevronRight className="w-4 h-4" />
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={loadData}
          className="btn-3d"
          title="Reload (pulls fresh lottery values from Daily Entry)"
          data-testid="sp-reload-btn"
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          <span className="text-xs">Reload</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={undo}
          disabled={!canUndo}
          className="btn-3d"
          title="Undo (Ctrl+Z)"
          data-testid="sp-undo-btn"
        >
          <Undo2 className="w-4 h-4 mr-1" />
          <span className="text-xs">Undo</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={redo}
          disabled={!canRedo}
          className="btn-3d"
          title="Redo (Ctrl+Shift+Z or Ctrl+Y)"
          data-testid="sp-redo-btn"
        >
          <Redo2 className="w-4 h-4 mr-1" />
          <span className="text-xs">Redo</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={clearPage}
          className="btn-3d border-rose-500/50 text-rose-300 hover:text-rose-200"
          title="Clear all amounts on this date (row layout preserved). You can Undo."
          data-testid="sp-clear-btn"
        >
          <Eraser className="w-4 h-4 mr-1" />
          <span className="text-xs">Clear This Page</span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => window.print()}
          className="btn-3d"
          title="Print this sheet"
          data-testid="sp-print-btn"
        >
          <Printer className="w-4 h-4 mr-1" />
          <span className="text-xs">Print</span>
        </Button>

        <ExportButton section="sp" currentDate={selectedDate} testIdPrefix="sp-export" />

        <Button
          variant="outline"
          size="sm"
          onClick={saveAndMaybeSyncLayout}
          disabled={saveStatus === "saving" || syncingLayout}
          className="btn-3d"
          title={isAdmin ? "Save this date, then propagate its row layout to all your other dates" : "Save Now"}
          data-testid="sp-save-btn"
        >
          <Save className="w-4 h-4 mr-1" />
          <span className="text-xs">
            {saveStatus === "saving" ? "Saving…" : syncingLayout ? "Syncing…" : saveStatus === "saved" ? "Saved" : saveStatus === "error" ? "Save Failed" : "Save"}
          </span>
        </Button>
        {isAdmin && (
          <div
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-mono border ${
              syncStatus === "syncing"
                ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                : syncStatus === "error"
                ? "border-rose-500/50 bg-rose-500/10 text-rose-300"
                : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
            }`}
            title={
              syncStatus === "syncing"
                ? "Propagating this date's layout to all other dates + shared template…"
                : syncStatus === "error"
                ? "Last layout sync failed. Click Sync Layout → All Dates to retry."
                : "Layout auto-sync is on. Any row change on any date propagates to all your dates + all users."
            }
            data-testid="sp-sync-status"
          >
            {syncStatus === "syncing" ? (
              <RefreshCw className="w-3 h-3 animate-spin" />
            ) : syncStatus === "error" ? (
              <span className="inline-block w-2 h-2 rounded-full bg-rose-400" />
            ) : (
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" />
            )}
            <span>
              {syncStatus === "syncing" ? "Syncing layout…" : syncStatus === "error" ? "Sync error" : "Layout synced"}
            </span>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-emerald-900/40 gap-2 p-1 h-auto" data-testid="sp-tabs-list">
          <TabsTrigger
            value="z-report"
            className="sp-3d-tab font-mono text-xs px-4 py-2 rounded-lg data-[state=active]:sp-3d-tab-active"
            data-testid="sp-tab-z-report"
          >
            Z-Report
          </TabsTrigger>
          <TabsTrigger
            value="reports"
            className="sp-3d-tab font-mono text-xs px-4 py-2 rounded-lg data-[state=active]:sp-3d-tab-active"
            data-testid="sp-tab-reports"
          >
            Reports
          </TabsTrigger>
          <TabsTrigger
            value="income-expense"
            className="sp-3d-tab font-mono text-xs px-4 py-2 rounded-lg data-[state=active]:sp-3d-tab-active"
            data-testid="sp-tab-income-expense"
          >
            Income &amp; Expenditure
          </TabsTrigger>
          <TabsTrigger
            value="pnl"
            className="sp-3d-tab font-mono text-xs px-4 py-2 rounded-lg data-[state=active]:sp-3d-tab-active"
            data-testid="sp-tab-pnl"
          >
            Profit &amp; Loss
          </TabsTrigger>
          <TabsTrigger
            value="statement"
            className="sp-3d-tab font-mono text-xs px-4 py-2 rounded-lg data-[state=active]:sp-3d-tab-active"
            data-testid="sp-tab-statement"
          >
            Statement
          </TabsTrigger>
          <TabsTrigger
            value="setup"
            className="sp-3d-tab font-mono text-xs px-4 py-2 rounded-lg data-[state=active]:sp-3d-tab-active"
            data-testid="sp-tab-setup"
          >
            Setup
          </TabsTrigger>
        </TabsList>

        <TabsContent value="z-report" className="space-y-4 mt-0" data-testid="sp-zreport-panel">

      {dateStr !== format(new Date(), "yyyy-MM-dd") && (
        <div className="bg-amber-500/20 border-2 border-amber-500/70 rounded-lg px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid="sp-not-today-banner">
          <span className="font-mono text-sm text-amber-200">
            <strong className="text-amber-100">Heads up:</strong> you're entering paperwork for{" "}
            <strong className="text-white">{format(selectedDate, "EEE, MMM d, yyyy")}</strong> — not today.
          </span>
          <Button
            size="sm"
            onClick={() => setSelectedDate(new Date())}
            className="h-7 bg-amber-500 text-black hover:bg-amber-400 font-mono font-bold"
            data-testid="sp-go-to-today-btn"
          >
            Go to Today
          </Button>
        </div>
      )}
      <DateWarningDialog
        pendingDate={pendingDate}
        onConfirm={() => { setSelectedDate(pendingDate); setPendingDate(null); }}
        onToday={() => { setSelectedDate(new Date()); setPendingDate(null); }}
        onCancel={() => setPendingDate(null)}
        testIdPrefix="sp-date-warning"
      />

      {/* Over / Short reconciliation — pinned at the top, visible without scrolling */}
      <Card
        className={`border-2 ${Math.abs(overShort) < 0.005 ? "border-emerald-500/60 bg-emerald-500/10" : overShort < 0 ? "border-rose-500/60 bg-rose-500/10" : "border-amber-500/60 bg-amber-500/10"}`}
        data-testid="section-over-short"
      >
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-xs text-muted-foreground tracking-wider uppercase">Reconciliation</p>
            <p className="font-mono text-sm text-card-foreground">Credit − Debit = Over / Short</p>
          </div>
          <div className="flex items-center gap-3 sm:gap-6 font-mono">
            <div className="flex flex-col items-end">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Credit</span>
              <span className="text-emerald-400 font-bold tabular-nums text-lg">{fmtMoney(creditTotal)}</span>
            </div>
            <span className="text-2xl text-muted-foreground">−</span>
            <div className="flex flex-col items-end">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Debit</span>
              <span className="text-rose-400 font-bold tabular-nums text-lg">{fmtMoney(debitTotal)}</span>
            </div>
            <span className="text-2xl text-muted-foreground">=</span>
            <div className="flex flex-col items-end">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                {Math.abs(overShort) < 0.005 ? "Balanced" : overShort < 0 ? "Short" : "Over"}
              </span>
              <span
                className={`font-bold tabular-nums text-2xl ${Math.abs(overShort) < 0.005 ? "text-emerald-400" : overShort < 0 ? "text-rose-400" : "text-amber-400"}`}
                data-testid="over-short-value"
              >
                {fmtMoneySigned(overShort)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inside Sales + Bank Deposit + Tax summary compact info window */}
      <Card className="border border-sky-500/40 bg-sky-500/5" data-testid="section-info-window">
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid="info-inside-sales">
            <div>
              <p className="font-mono text-[11px] text-sky-300 tracking-wider uppercase">Inside Sales</p>
              <p className="font-mono text-[10px] text-muted-foreground">Debit total excl. lottery, fuel, money orders &amp; taxes</p>
            </div>
            <span className="font-mono text-2xl font-bold tabular-nums text-sky-300" data-testid="inside-sales-value">
              {fmtMoney(insideSalesTotal)}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:border-l sm:border-sky-500/30 sm:pl-4" data-testid="info-bank-deposit">
            <div>
              <p className="font-mono text-[11px] text-emerald-300 tracking-wider uppercase">Bank Deposit</p>
              <p className="font-mono text-[10px] text-muted-foreground">Total Cash + Total Checks only</p>
            </div>
            <span className="font-mono text-2xl font-bold tabular-nums text-emerald-300" data-testid="bank-deposit-value">
              {fmtMoney(bankDepositTotal)}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 xl:border-l xl:border-sky-500/30 xl:pl-4" data-testid="info-taxable-depts">
            <div>
              <p className="font-mono text-[11px] text-amber-300 tracking-wider uppercase">Taxable Depts</p>
              <p className="font-mono text-[10px] text-muted-foreground">Σ rows suffixed (T) or (Beer T)</p>
            </div>
            <span className="font-mono text-2xl font-bold tabular-nums text-amber-300" data-testid="taxable-depts-value">
              {fmtMoney(taxableDeptTotal)}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:border-l sm:border-sky-500/30 sm:pl-4" data-testid="info-tax-total">
            <div>
              <p className="font-mono text-[11px] text-rose-300 tracking-wider uppercase">Total Tax</p>
              <p className="font-mono text-[10px] text-muted-foreground">State Tax + City Beer Tax</p>
            </div>
            <span className="font-mono text-2xl font-bold tabular-nums text-rose-300" data-testid="tax-total-value">
              {fmtMoney(taxTotal)}
            </span>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center p-12 text-muted-foreground font-mono text-sm" data-testid="sp-loading">
          Loading store paperwork…
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* Debit — Z Report (editable rows) */}
          <Card className="border-rose-500/40 bg-rose-500/5" data-testid="section-z-report">
            <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 flex-wrap">
              <CardTitle className="font-mono text-sm">Debit — Departments (Entries from &quot;Z&quot; Report)</CardTitle>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {isAdmin && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={fixLayoutForAdmin}
                      className="btn-3d h-7 border-sky-500/60 text-sky-300 hover:text-sky-200"
                      title="Move Paid-Out rows to Credit and group lottery rows at top of Debit — THIS DATE ONLY"
                      data-testid="admin-fix-layout-btn"
                    >
                      <Wand2 className="w-3.5 h-3.5 mr-1" /> Fix Layout
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={bulkFixAllLayouts}
                      disabled={bulkFixing}
                      className="btn-3d h-7 border-amber-500/60 text-amber-300 hover:text-amber-200"
                      title="Same as Fix Layout but applied to ALL your past Store Paperwork dates in one sweep"
                      data-testid="admin-fix-layout-all-btn"
                    >
                      <Wand2 className="w-3.5 h-3.5 mr-1" />
                      {bulkFixing ? "Fixing all…" : "Fix ALL Past"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={bulkFixAllUsers}
                      disabled={bulkFixingAllUsers}
                      className="btn-3d h-7 border-rose-500/60 text-rose-300 hover:text-rose-200"
                      title="Fix Store Paperwork layout for EVERY user in the database, on every date — collapses legacy $1–$50 rows, moves paid-outs to Credit, dedupes auto-row duplicates"
                      data-testid="admin-fix-layout-all-users-btn"
                    >
                      <Wand2 className="w-3.5 h-3.5 mr-1" />
                      {bulkFixingAllUsers ? "Fixing all users…" : "Fix ALL Users"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => syncLayoutToAllDates({ silent: false })}
                      disabled={syncingLayout}
                      className="btn-3d h-7 border-emerald-500/60 text-emerald-300 hover:text-emerald-200"
                      title="Copy this date's row names + order to EVERY other saved date. Amounts on each date are preserved by name."
                      data-testid="admin-sync-layout-btn"
                    >
                      <Wand2 className="w-3.5 h-3.5 mr-1" />
                      {syncingLayout ? "Syncing…" : "Sync Layout → All Dates"}
                    </Button>
                  </>
                )}
                <span className="font-mono text-xs text-muted-foreground">
                  Total <strong className="text-rose-400 tabular-nums text-base ml-1" data-testid="debit-total">{fmtMoney(debitTotal)}</strong>
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <EditableRowList
                rows={debitRows}
                onChange={(rows) => update("debit_rows", rows)}
                testIdPrefix="debit"
                labelHeader="Department"
                placeholderName='Department (suffix "(T)" = state taxable, "(Beer T)" = beer taxable)'
                templateNames={debitTemplateNames}
                getRowMeta={(row) => {
                  if (row?._kind === STATE_TAX_KIND) {
                    return { readOnlyAmount: true, readOnlyName: true, hideDelete: true, badge: `auto ${taxSettings.state_tax_percent}%` };
                  }
                  if (row?._kind === BEER_TAX_KIND) {
                    return { readOnlyAmount: true, readOnlyName: true, hideDelete: true, badge: `auto ${taxSettings.beer_tax_percent}%` };
                  }
                  if (row?._kind === TOTAL_FUEL_KIND) {
                    return {
                      readOnlyAmount: true,
                      readOnlyName: true,
                      hideDelete: true,
                      badge: "from Fuel Section",
                      readOnlyVariant: "lottery",
                    };
                  }
                  // Lottery denomination row: name matches "$N Instant Lottery"
                  // → force read-only, pull amount from Daily Entry, sky-blue tint.
                  const d = detectLotteryDenom(row?.name);
                  if (d != null) {
                    return {
                      readOnlyAmount: true,
                      readOnlyName: true,
                      hideDelete: true,
                      badge: `from Lottery Mgmt`,
                      readOnlyVariant: "lottery",
                    };
                  }
                  return null;
                }}
              />
            </CardContent>
          </Card>

          {/* Credit — Network / EFT (editable rows) */}
          <Card className="border-emerald-500/40 bg-emerald-500/5" data-testid="section-network">
            <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
              <CardTitle className="font-mono text-sm">Credit — Network / EFT</CardTitle>
              <span className="font-mono text-xs text-muted-foreground">
                Total <strong className="text-emerald-400 tabular-nums text-base ml-1" data-testid="credit-total">{fmtMoney(creditTotal)}</strong>
              </span>
            </CardHeader>
            <CardContent>
              <EditableRowList
                rows={creditRows}
                onChange={(rows) => update("credit_rows", rows)}
                testIdPrefix="credit"
                labelHeader="Item"
                placeholderName="Item name"
                templateNames={creditTemplateNames}
                getRowMeta={(row) => {
                  if (row?._kind === VENDORS_CASH_KIND) {
                    return {
                      readOnlyAmount: true,
                      readOnlyName: true,
                      hideDelete: true,
                      badge: "from Suppliers",
                      readOnlyVariant: "lottery",
                    };
                  }
                  // Lottery-auto rows work on the Credit side too (admin
                  // templates commonly put "Instant Paid Out" /
                  // "Online Paid Out" over here).
                  const d = detectLotteryDenom(row?.name);
                  if (d != null) {
                    return {
                      readOnlyAmount: true,
                      readOnlyName: true,
                      hideDelete: true,
                      badge: `from Lottery Mgmt`,
                      readOnlyVariant: "lottery",
                    };
                  }
                  return null;
                }}
              />
            </CardContent>
          </Card>

          {/* Supplier Section — cash paid out flows into "Vendors Cash Paid Out" debit row */}
          <div className="xl:col-span-2">
            <SupplierSection
              rows={data.supplier_rows || []}
              vendors={vendors}
              onChange={(rows) => update("supplier_rows", rows)}
              onAddVendor={addVendor}
              cashTotal={supplierCashTotal}
            />
          </div>

          {/* Fuel Tank Report + Tank Monitor — Sold Amount total drives the "Total Fuel" debit auto-row */}
          <div className="xl:col-span-2">
            <FuelSection
              rows={data.fuel_rows || DEFAULT_FUEL_ROWS}
              onChange={(rows) => update("fuel_rows", rows)}
              totalSoldAmount={totalFuelSoldAmount}
            />
          </div>

        </div>
      )}

      {/* Print-only footer line so the date prints clearly */}
      <div className="hidden print:block text-center text-xs font-mono mt-4" data-testid="print-footer">
        Store Paperwork · {dateStr}
      </div>
        </TabsContent>

        <TabsContent value="reports" className="mt-0" data-testid="sp-reports-panel">
          <div className="space-y-6">
            {/* Monthly Data Sheet — one row per day with the key daily figures
                (taxable/non-taxable sales, tax, tenders, lottery, gasoline,
                bank deposit, over/short) plus Average and Total rows. */}
            <section>
              <h3 className="font-mono text-sm font-bold text-primary mb-2 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-sky-400" />
                Monthly Data Sheet
              </h3>
              <MonthlyDataSheet />
            </section>

            {/* Item Tracker — build a queue of specific items (e.g. Cigarette
                Packs, State Tax, Network Revenue). The queue also drives the
                Monthly Data Sheet columns above. */}
            <section>
              <h3 className="font-mono text-sm font-bold text-primary mb-2 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
                Item Tracker
              </h3>
              <ItemTrackerReport />
            </section>
          </div>
        </TabsContent>

        <TabsContent value="income-expense" className="mt-0" data-testid="sp-income-expense-panel">
          <IncomeExpenseTab highlightId={highlightIeId} />
        </TabsContent>

        <TabsContent value="pnl" className="mt-0" data-testid="sp-pnl-panel">
          <ProfitLossTab />
        </TabsContent>

        <TabsContent value="statement" className="mt-0" data-testid="sp-statement-panel">
          <AccountStatementTab
            onOpenPaperworkDate={(d) => {
              const parsed = parseISO(d);
              if (!isNaN(parsed.getTime())) setSelectedDate(parsed);
              setActiveTab("z-report");
            }}
            onOpenManualEntry={(entryId) => {
              setHighlightIeId(entryId || null);
              setActiveTab("income-expense");
            }}
          />
        </TabsContent>

        <TabsContent value="setup" className="mt-0" data-testid="sp-setup-panel">
          <SetupPanel
            taxSettings={taxSettings}
            taxSaving={taxSaving}
            isAdmin={false}
            vendors={vendors}
            onAddVendor={addVendor}
            onRenameVendor={renameVendor}
            onDeleteVendor={deleteVendor}
            onSave={async (patch) => {
              setTaxSaving(true);
              try {
                const res = await axios.put(`${API}/store-paperwork-tax-settings`, patch);
                setTaxSettings(res.data);
                toast.success("Tax settings saved");
              } catch (err) {
                toast.error("Failed to save tax settings");
              } finally {
                setTaxSaving(false);
              }
            }}
          />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}

// Re-usable section card with footer total
function Section({ title, total, children, testId }) {
  return (
    <Card className="border-border/50" data-testid={testId}>
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
        <CardTitle className="font-mono text-sm">{title}</CardTitle>
        {total && (
          <span className="font-mono text-xs text-muted-foreground">Total: <strong className="text-primary tabular-nums">{total}</strong></span>
        )}
      </CardHeader>
      <CardContent className="py-2">{children}</CardContent>
    </Card>
  );
}

// Setup tab — per-user tax percentages used to auto-calculate (T) / Beer T) rows,
// plus per-user vendor master list used by the Supplier Section on the Z-Report.
function SetupPanel({ taxSettings, taxSaving, onSave, vendors = [], onAddVendor, onRenameVendor, onDeleteVendor }) {
  const [stateInput, setStateInput] = useState(String(taxSettings.state_tax_percent));
  const [beerInput, setBeerInput] = useState(String(taxSettings.beer_tax_percent));

  // Keep local inputs in sync when the upstream value changes (e.g. reset).
  useEffect(() => {
    setStateInput(String(taxSettings.state_tax_percent));
    setBeerInput(String(taxSettings.beer_tax_percent));
  }, [taxSettings.state_tax_percent, taxSettings.beer_tax_percent]);

  const stateOver = taxSettings.is_user_override?.state_tax_percent;
  const beerOver = taxSettings.is_user_override?.beer_tax_percent;
  const stateDefault = taxSettings.defaults?.state_tax_percent;
  const beerDefault = taxSettings.defaults?.beer_tax_percent;

  const saveAll = () => onSave({
    state_tax_percent: parseFloat(stateInput),
    beer_tax_percent: parseFloat(beerInput),
  });
  const resetField = (key) => onSave({ [key]: null });

  return (
    <div className="space-y-4 max-w-2xl">
      <Card className="border-emerald-500/30 bg-emerald-900/30" data-testid="sp-setup-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-mono text-sm text-card-foreground">Tax Auto-Calculation</CardTitle>
          <p className="font-mono text-[11px] text-card-foreground/80 mt-1">
            Tag any Department row by adding a suffix to its name:{" "}
            <code className="px-1 rounded bg-emerald-700 text-amber-200">(T)</code> = state-taxable,{" "}
            <code className="px-1 rounded bg-emerald-700 text-amber-200">(Beer T)</code> = beer (both state AND city beer tax).
            The cashier still types the dollar amount manually for these rows. Two pinned auto-rows at the BOTTOM of the
            Debit list show the calculated tax: <strong>State Tax = {taxSettings.state_tax_percent}% × sum of (T) + (Beer T) rows</strong>{" "}
            and <strong>City Beer Tax = {taxSettings.beer_tax_percent}% × sum of (Beer T) rows only</strong>. Beer rows pay
            BOTH state tax and city beer tax. Auto cells render with an amber background and a small{" "}
            <code className="px-1 rounded bg-amber-500 text-black">auto N%</code> badge.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* State tax */}
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-3">
              <label className="font-mono text-xs text-card-foreground" htmlFor="sp-state-tax-input">
                State Tax %
              </label>
              <span className="text-[10px] font-mono text-card-foreground/70">
                {stateOver ? `Custom (default ${stateDefault}%)` : `Using default ${stateDefault}%`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Input
                id="sp-state-tax-input"
                inputMode="decimal"
                value={stateInput}
                onChange={(e) => setStateInput(e.target.value)}
                className="h-8 max-w-[120px] text-right font-mono !bg-yellow-100 !text-black !border-stone-400"
                data-testid="sp-state-tax-input"
                placeholder="6"
              />
              <span className="font-mono text-xs text-card-foreground">%</span>
              {stateOver && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => resetField("state_tax_percent")}
                  disabled={taxSaving}
                  className="h-8"
                  data-testid="sp-state-tax-reset"
                >
                  Reset to default
                </Button>
              )}
            </div>
          </div>

          {/* City Beer tax */}
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-3">
              <label className="font-mono text-xs text-card-foreground" htmlFor="sp-beer-tax-input">
                City Beer Tax %
              </label>
              <span className="text-[10px] font-mono text-card-foreground/70">
                {beerOver ? `Custom (default ${beerDefault}%)` : `Using default ${beerDefault}%`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Input
                id="sp-beer-tax-input"
                inputMode="decimal"
                value={beerInput}
                onChange={(e) => setBeerInput(e.target.value)}
                className="h-8 max-w-[120px] text-right font-mono !bg-yellow-100 !text-black !border-stone-400"
                data-testid="sp-beer-tax-input"
                placeholder="5"
              />
              <span className="font-mono text-xs text-card-foreground">%</span>
              {beerOver && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => resetField("beer_tax_percent")}
                  disabled={taxSaving}
                  className="h-8"
                  data-testid="sp-beer-tax-reset"
                >
                  Reset to default
                </Button>
              )}
            </div>
          </div>

          <div className="pt-2">
            <Button
              onClick={saveAll}
              disabled={taxSaving}
              className="btn-3d"
              data-testid="sp-tax-save-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              {taxSaving ? "Saving…" : "Save tax settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/40 bg-emerald-900/20" data-testid="sp-setup-example">
        <CardHeader className="pb-2">
          <CardTitle className="font-mono text-xs text-card-foreground/90 uppercase tracking-wider">
            Naming examples
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 font-mono text-[11px] text-card-foreground/80">
          <p><code className="px-1 rounded bg-emerald-700 text-amber-200">Snacks</code> — regular row, NOT taxed.</p>
          <p><code className="px-1 rounded bg-emerald-700 text-amber-200">Cigarettes (T)</code> — state-taxable row; cashier types $ manually; counted toward the State Tax base.</p>
          <p><code className="px-1 rounded bg-emerald-700 text-amber-200">Beer Cooler (Beer T)</code> — beer-taxable row; cashier types $ manually; counted toward BOTH the State Tax base AND the City Beer Tax base.</p>
          <p><code className="px-1 rounded bg-amber-500 text-black">State Tax ({taxSettings.state_tax_percent}%)</code> — pinned auto-row at bottom of Debit list. Read-only.</p>
          <p><code className="px-1 rounded bg-amber-500 text-black">City Beer Tax ({taxSettings.beer_tax_percent}%)</code> — pinned auto-row at bottom of Debit list. Read-only.</p>
        </CardContent>
      </Card>

      <VendorMasterCard
        vendors={vendors}
        onAdd={onAddVendor}
        onRename={onRenameVendor}
        onDelete={onDeleteVendor}
      />
    </div>
  );
}

// ---- Vendor master list card (rendered inside SetupPanel) ------------------
function VendorMasterCard({ vendors, onAdd, onRename, onDelete }) {
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!newName.trim() || busy) return;
    setBusy(true);
    await onAdd(newName);
    setNewName("");
    setBusy(false);
  };
  return (
    <Card className="border-sky-500/40 bg-sky-900/20" data-testid="sp-vendors-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-mono text-sm text-card-foreground flex items-center gap-2">
          <Store className="w-4 h-4 text-sky-300" />
          Vendor Master List
        </CardTitle>
        <p className="font-mono text-[11px] text-card-foreground/80 mt-1">
          Any user (regular or admin) can add vendors here. Used by the{" "}
          <strong>Supplier</strong> section on the Z-Report — pick a vendor from
          this list + a payment method (Cash / Check / ACH) + amount. Only{" "}
          <strong>Cash</strong> supplier payments flow into the pinned
          &ldquo;Vendors Cash Paid Out&rdquo; debit row.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder="Vendor name (e.g. Coca-Cola, Frito-Lay)"
            className="!bg-yellow-100 !text-black !border-stone-400 h-8 flex-1 font-mono text-xs"
            data-testid="sp-vendor-new-input"
          />
          <Button
            size="sm"
            onClick={submit}
            disabled={busy || !newName.trim()}
            data-testid="sp-vendor-add"
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
        {vendors.length === 0 ? (
          <p className="font-mono text-xs text-card-foreground/60 text-center py-3">
            No vendors yet. Add your first one above.
          </p>
        ) : (
          <div className="space-y-1" data-testid="sp-vendor-list">
            {vendors.map((v) => (
              <VendorRow
                key={v.id}
                vendor={v}
                onRename={onRename}
                onDelete={onDelete}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function VendorRow({ vendor, onRename, onDelete }) {
  const [name, setName] = useState(vendor.name);
  useEffect(() => { setName(vendor.name); }, [vendor.name]);
  const dirty = name.trim() !== vendor.name && name.trim() !== "";
  return (
    <div className="flex items-center gap-2" data-testid={`sp-vendor-row-${vendor.id}`}>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => { if (dirty) onRename(vendor.id, name); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { if (dirty) onRename(vendor.id, name); e.currentTarget.blur(); }
          if (e.key === "Escape") setName(vendor.name);
        }}
        className="!bg-yellow-100 !text-black !border-stone-400 h-8 flex-1 font-mono text-xs"
        data-testid={`sp-vendor-name-${vendor.id}`}
      />
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-rose-400 hover:text-rose-300"
        onClick={() => {
          if (window.confirm(`Remove vendor "${vendor.name}"?`)) onDelete(vendor.id);
        }}
        data-testid={`sp-vendor-delete-${vendor.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

// ---- Supplier Section (Z-Report tab) --------------------------------------
// Editable list of supplier rows: [vendor dropdown] × [Cash/Check/ACH] × [amount].
// Cash-method rows are summed and displayed in the pinned "Vendors Cash Paid Out"
// debit row (see applyAutoTaxRows in this file).
function SupplierSection({ rows, vendors, onChange, onAddVendor, cashTotal }) {
  const list = Array.isArray(rows) ? rows : [];
  const totalByMethod = useMemo(() => {
    const acc = { cash: 0, check: 0, ach: 0 };
    for (const r of list) {
      if (!r) continue;
      // Missing/blank method displays as "Cash" in the UI (SupplierRow uses
      // `row.method || "cash"` as the select value fallback) — mirror that
      // here so the header totals match the Debit "Vendors Cash Paid Out"
      // row exactly.
      const m = (r.method || "cash").toLowerCase();
      if (m in acc) acc[m] += num(r.amount);
    }
    return acc;
  }, [list]);

  const updateRow = (idx, patch) => {
    const next = list.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };
  const removeRow = (idx) => onChange(list.filter((_, i) => i !== idx));
  const addRow = () => onChange([...list, { id: `sup-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, vendor_id: "", vendor_name: "", method: "cash", amount: "" }]);

  return (
    <Card className="border-sky-500/40 bg-sky-500/5" data-testid="section-suppliers">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 flex-wrap">
        <CardTitle className="font-mono text-sm flex items-center gap-2">
          <Store className="w-4 h-4 text-sky-300" />
          Supplier Payments
        </CardTitle>
        <div className="flex items-center gap-4 font-mono text-[11px] text-card-foreground/90">
          <span>Cash: <strong className="text-emerald-300 tabular-nums">{fmtMoney(totalByMethod.cash)}</strong></span>
          <span>Check: <strong className="text-amber-300 tabular-nums">{fmtMoney(totalByMethod.check)}</strong></span>
          <span>ACH: <strong className="text-sky-300 tabular-nums">{fmtMoney(totalByMethod.ach)}</strong></span>
          <span className="hidden sm:inline text-muted-foreground">→ Debit &ldquo;Vendors Cash Paid Out&rdquo; ={" "}
            <strong className="text-rose-300 tabular-nums">{fmtMoney(cashTotal)}</strong>
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {list.length === 0 ? (
          <p className="font-mono text-xs text-card-foreground/60 text-center py-4">
            No supplier payments yet. Click &ldquo;Add Payment&rdquo; to record a check/cash/ACH paid to a vendor.
          </p>
        ) : (
          <div className="space-y-1.5">
            {/* header row */}
            <div className="grid grid-cols-[minmax(0,1fr)_140px_140px_36px] gap-2 items-center text-[10px] uppercase tracking-wider font-mono text-card-foreground/70 pb-1 border-b border-border/40">
              <span>Vendor</span>
              <span>Method</span>
              <span className="text-right pr-2">Amount</span>
              <span />
            </div>
            {list.map((r, i) => (
              <SupplierRow
                key={r.id || i}
                idx={i}
                row={r}
                vendors={vendors}
                onChange={updateRow}
                onRemove={removeRow}
                onAddVendor={onAddVendor}
              />
            ))}
          </div>
        )}
        <div className="pt-2">
          <Button
            size="sm"
            variant="outline"
            onClick={addRow}
            className="border-sky-500/50"
            data-testid="supplier-add-row"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Payment
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SupplierRow({ idx, row, vendors, onChange, onRemove, onAddVendor }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newVendorName, setNewVendorName] = useState("");

  const commitVendor = (vid) => {
    const v = vendors.find((x) => x.id === vid);
    onChange(idx, { vendor_id: vid, vendor_name: v ? v.name : "" });
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_140px_140px_36px] gap-2 items-center" data-testid={`supplier-row-${idx}`}>
      {/* Vendor dropdown */}
      {showAdd ? (
        <div className="flex items-center gap-1">
          <Input
            autoFocus
            value={newVendorName}
            onChange={(e) => setNewVendorName(e.target.value)}
            placeholder="New vendor name"
            className="!bg-yellow-100 !text-black !border-stone-400 h-8 flex-1 font-mono text-xs"
            data-testid={`supplier-new-vendor-input-${idx}`}
            onKeyDown={async (e) => {
              if (e.key === "Enter" && newVendorName.trim()) {
                const v = await onAddVendor(newVendorName);
                if (v) { commitVendor(v.id); }
                setNewVendorName(""); setShowAdd(false);
              }
              if (e.key === "Escape") { setNewVendorName(""); setShowAdd(false); }
            }}
          />
          <Button
            size="sm"
            className="h-8"
            onClick={async () => {
              if (!newVendorName.trim()) return;
              const v = await onAddVendor(newVendorName);
              if (v) commitVendor(v.id);
              setNewVendorName(""); setShowAdd(false);
            }}
            data-testid={`supplier-new-vendor-save-${idx}`}
          >Save</Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => { setNewVendorName(""); setShowAdd(false); }}>Cancel</Button>
        </div>
      ) : (
        <select
          value={row.vendor_id || ""}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "__add__") { setShowAdd(true); return; }
            commitVendor(val);
          }}
          className="h-8 rounded-md border !bg-yellow-100 !text-black !border-stone-400 font-mono text-xs px-2"
          data-testid={`supplier-vendor-select-${idx}`}
        >
          <option value="">— select vendor —</option>
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
          <option value="__add__">＋ Add new vendor…</option>
        </select>
      )}
      {/* Payment method */}
      <select
        value={row.method || "cash"}
        onChange={(e) => onChange(idx, { method: e.target.value })}
        className={`h-8 rounded-md border font-mono text-xs px-2 !text-black !border-stone-400 ${row.method === "cash" ? "!bg-emerald-100" : row.method === "check" ? "!bg-amber-100" : "!bg-sky-100"}`}
        data-testid={`supplier-method-${idx}`}
      >
        {SUPPLIER_METHODS.map((m) => (
          <option key={m} value={m}>{SUPPLIER_METHOD_LABELS[m]}</option>
        ))}
      </select>
      {/* Amount */}
      <Input
        inputMode="decimal"
        value={row.amount || ""}
        onChange={(e) => onChange(idx, { amount: e.target.value })}
        placeholder="0.00"
        className="h-8 text-right text-xs font-mono tabular-nums !text-black !bg-yellow-100 !border-stone-400"
        data-testid={`supplier-amount-${idx}`}
      />
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-rose-400 hover:text-rose-300"
        onClick={() => onRemove(idx)}
        data-testid={`supplier-remove-${idx}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}


// ─── FuelSection ────────────────────────────────────────────────────────────
// Renders the Fuel Tank Report + Tank Monitor table shown in the user's
// mockup. Fixed list of 5 fuel grades (UNL Regular / Plus / Premium / Diesel
// / Kerosene). Two computed columns per row:
//   • Sell Price = sold_amount / sold_volume  (read-only)
//   • O/S        = in_tank_volume + fuel_delivery − sold_volume  (read-only)
// Bottom "Total Gasoline" strip sums Sold Volume, Sold Amount, In Tank Volume
// and Fuel Delivery. The Sold Amount total drives the auto "Total Fuel" row
// on the Debit side (wired up outside this component via `totalFuelSoldAmount`
// → `applyAutoTaxRows`).
function FuelSection({ rows, onChange, totalSoldAmount }) {
  const list = Array.isArray(rows) && rows.length > 0 ? rows : DEFAULT_FUEL_ROWS;

  const updateRow = (idx, patch) => {
    const next = list.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };

  const totals = useMemo(() => {
    let vol = 0, amt = 0, tank = 0, delivery = 0;
    for (const r of list) {
      vol += num(r?.sold_volume);
      amt += num(r?.sold_amount);
      tank += num(r?.in_tank_volume);
      delivery += num(r?.fuel_delivery);
    }
    return { vol, amt, tank, delivery };
  }, [list]);

  const computeSellPrice = (r) => {
    const v = num(r?.sold_volume);
    const a = num(r?.sold_amount);
    if (v <= 0) return null;
    return a / v;
  };
  const computeOS = (r) => num(r?.in_tank_volume) + num(r?.fuel_delivery) - num(r?.sold_volume);

  const NUMBER_INPUT = "h-8 text-xs font-mono tabular-nums text-right bg-yellow-50/90 dark:bg-yellow-500/10 border-yellow-500/40 dark:border-yellow-500/30 !text-black";
  const READONLY_INPUT = "h-8 text-xs font-mono tabular-nums text-right bg-muted/60 border-muted-foreground/20 !text-black cursor-not-allowed";
  const TANK_INPUT = "h-8 text-xs font-mono tabular-nums text-right bg-orange-50/90 dark:bg-orange-500/10 border-orange-500/40 dark:border-orange-500/30 !text-black";
  const LABEL_CELL = "h-8 px-2 flex items-center text-xs font-mono font-semibold bg-orange-200/60 dark:bg-orange-400/20 border border-orange-500/40 rounded !text-black";
  const TANK_LABEL_CELL = "h-8 px-2 flex items-center text-xs font-mono font-semibold bg-sky-400/70 dark:bg-sky-500/40 border border-sky-500/50 rounded !text-black";
  const GRID_COLS = "grid grid-cols-[130px,80px,90px,80px,80px,80px,14px,110px,80px,80px,70px] gap-1";

  return (
    <Card className="border-amber-500/40 bg-amber-500/5" data-testid="section-fuel">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 flex-wrap">
        <CardTitle className="font-mono text-sm flex items-center gap-2">
          <FileText className="w-4 h-4 text-amber-300" />
          Fuel Tank Report &amp; Tank Monitor
        </CardTitle>
        <span className="font-mono text-xs text-muted-foreground">
          Total Sold Amount{" "}
          <strong className="text-emerald-400 tabular-nums text-base ml-1" data-testid="fuel-total-sold-amount-header">
            {fmtMoney(totalSoldAmount)}
          </strong>
          <span className="ml-2 text-[10px] text-sky-300">→ Debit &ldquo;Total Fuel&rdquo;</span>
        </span>
      </CardHeader>
      <CardContent className="pt-1">
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            <div className={`${GRID_COLS} pb-2 text-[10px] font-mono uppercase text-black dark:text-black tracking-wider text-center items-center`}>
              <span>Fuel Tank Report</span>
              <span>Sold Volume</span>
              <span>Sold Amount</span>
              <span>Sell Price</span>
              <span>Sign Price</span>
              <span>Cost Price</span>
              <span />
              <span>Tank Monitor</span>
              <span>In Tank Volume</span>
              <span>Fuel Delivery</span>
              <span>O/S</span>
            </div>
            <div className="space-y-1">
              {list.map((r, i) => {
                const sp = computeSellPrice(r);
                const os = computeOS(r);
                return (
                  <div key={r?.id || `fuel-row-${i}`} className={`${GRID_COLS} items-center`} data-testid={`fuel-row-${i}`}>
                    <div className={LABEL_CELL} data-testid={`fuel-name-${i}`}>{r?.name || ""}</div>
                    <Input
                      value={r?.sold_volume || ""}
                      onChange={(e) => updateRow(i, { sold_volume: e.target.value })}
                      className={NUMBER_INPUT}
                      placeholder="0.000"
                      inputMode="decimal"
                      data-testid={`fuel-sold-volume-${i}`}
                    />
                    <div className="relative">
                      <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] font-mono text-muted-foreground pointer-events-none">$</span>
                      <Input
                        value={r?.sold_amount || ""}
                        onChange={(e) => updateRow(i, { sold_amount: e.target.value })}
                        className={NUMBER_INPUT + " pl-4"}
                        placeholder="0.00"
                        inputMode="decimal"
                        data-testid={`fuel-sold-amount-${i}`}
                      />
                    </div>
                    <Input
                      value={sp == null ? "" : sp.toFixed(3)}
                      readOnly
                      className={READONLY_INPUT}
                      title="Sell Price = Sold Amount ÷ Sold Volume"
                      data-testid={`fuel-sell-price-${i}`}
                    />
                    <Input
                      value={r?.sign_price || ""}
                      onChange={(e) => updateRow(i, { sign_price: e.target.value })}
                      className={NUMBER_INPUT}
                      placeholder="0.000"
                      inputMode="decimal"
                      data-testid={`fuel-sign-price-${i}`}
                    />
                    <Input
                      value={r?.cost_price || ""}
                      onChange={(e) => updateRow(i, { cost_price: e.target.value })}
                      className={NUMBER_INPUT}
                      placeholder="0.000"
                      inputMode="decimal"
                      data-testid={`fuel-cost-price-${i}`}
                    />
                    <div className="h-8 border-l border-fuchsia-500/50 mx-auto w-0" aria-hidden />
                    <div className={TANK_LABEL_CELL} data-testid={`fuel-tank-name-${i}`}>{r?.name?.replace(/^UNL\s+/i, "") || ""}</div>
                    <Input
                      value={r?.in_tank_volume || ""}
                      onChange={(e) => updateRow(i, { in_tank_volume: e.target.value })}
                      className={TANK_INPUT}
                      placeholder="0"
                      inputMode="decimal"
                      data-testid={`fuel-in-tank-volume-${i}`}
                    />
                    <Input
                      value={r?.fuel_delivery || ""}
                      onChange={(e) => updateRow(i, { fuel_delivery: e.target.value })}
                      className={NUMBER_INPUT}
                      placeholder="0"
                      inputMode="decimal"
                      data-testid={`fuel-fuel-delivery-${i}`}
                    />
                    <Input
                      value={fmtNum(os, 0)}
                      readOnly
                      className={READONLY_INPUT}
                      title="O/S = In Tank Volume + Fuel Delivery − Sold Volume"
                      data-testid={`fuel-os-${i}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className={`${GRID_COLS} items-center mt-2 pt-2 border-t border-border/40`}>
              <div className="h-8 px-2 flex items-center text-xs font-mono font-bold bg-muted/60 rounded !text-black">Total Gasoline</div>
              <div className="h-8 px-2 flex items-center justify-end text-xs font-mono font-bold bg-black text-emerald-400 tabular-nums rounded" data-testid="fuel-total-sold-volume">{fmtNum(totals.vol, 3)}</div>
              <div className="h-8 px-2 flex items-center justify-end text-xs font-mono font-bold bg-black text-emerald-400 tabular-nums rounded" data-testid="fuel-total-sold-amount">{fmtMoney(totals.amt)}</div>
              <div className="h-8" />
              <div className="h-8" />
              <div className="h-8" />
              <div />
              <div className="h-8 px-2 flex items-center text-xs font-mono font-bold bg-sky-400/70 dark:bg-sky-500/40 border border-sky-500/50 rounded !text-black">Total</div>
              <div className="h-8 px-2 flex items-center justify-end text-xs font-mono font-bold bg-black text-emerald-400 tabular-nums rounded" data-testid="fuel-total-in-tank-volume">{fmtNum(totals.tank, 0)}</div>
              <div className="h-8 px-2 flex items-center justify-end text-xs font-mono font-bold bg-black text-emerald-400 tabular-nums rounded" data-testid="fuel-total-fuel-delivery">{fmtNum(totals.delivery, 0)}</div>
              <div />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

